/**
 * Home banner logo: animate into header once on scroll down; stays in header after that.
 */

const BANNER_LOGO_TRANSITION = 'transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)';
const BODY_CLASS_DOCKED = 'is-logo-docked';
const BODY_CLASS_SCROLL_BACK = 'is-logo-scroll-back';
const DESKTOP_ANIMATION_MIN = 1200;
const animationMediaQuery = window.matchMedia(
	`(min-width: ${ DESKTOP_ANIMATION_MIN }px)`
);

function isAnimationEnabled() {
	return animationMediaQuery.matches;
}

function isHomeBannerPage() {
	return (
		document.body.classList.contains( 'home' ) ||
		document.body.classList.contains( 'front-page' ) ||
		!! document.querySelector( '.bts-banner--homepage' )
	);
}

function ensureBannerLogoPlaceholder( bannerLogoWrap ) {
	let placeholder = bannerLogoWrap.querySelector( '.banner-logo-placeholder' );

	if ( ! placeholder ) {
		placeholder = document.createElement( 'span' );
		placeholder.className = 'banner-logo-placeholder';
		placeholder.setAttribute( 'aria-hidden', 'true' );
		bannerLogoWrap.insertBefore( placeholder, bannerLogoWrap.firstChild );
	}

	return placeholder;
}

function clearPositionStyles( el ) {
	el.style.position = '';
	el.style.top = '';
	el.style.left = '';
	el.style.width = '';
	el.style.height = '';
	el.style.transform = '';
	el.style.transformOrigin = '';
	el.style.zIndex = '';
	el.style.margin = '';
	el.style.transition = '';
	el.style.opacity = '';
}

export default function initAnimations() {
	if ( ! isHomeBannerPage() ) {
		return;
	}

	const header = document.querySelector( 'header' );
	const headerLogoLink = document.querySelector( 'header .logo a' );
	const headerDefaultImg = headerLogoLink
		? headerLogoLink.querySelector( 'img:not(.banner-logo)' )
		: null;
	const bannerLogoWrap = document.querySelector( '.bts-banner--homepage .bts-banner__logo' );
	const bannerLogo = bannerLogoWrap
		? bannerLogoWrap.querySelector( '.banner-logo' )
		: null;

	if ( ! header || ! headerLogoLink || ! bannerLogoWrap || ! bannerLogo ) {
		return;
	}

	const placeholder = ensureBannerLogoPlaceholder( bannerLogoWrap );

	let state = 'idle';
	let homeMetrics = captureHomeMetrics();
	let lastScrollY = window.scrollY;

	function captureHomeMetrics() {
		const logoRect = bannerLogo.getBoundingClientRect();

		return {
			width: logoRect.width,
			height: logoRect.height,
		};
	}

	function setBodyDocked( active ) {
		document.body.classList.toggle( BODY_CLASS_DOCKED, active );
	}

	function setBodyScrollBack( active ) {
		document.body.classList.toggle( BODY_CLASS_SCROLL_BACK, active );
	}

	function updateScrollBackClass() {
		if ( state !== 'docked' ) {
			return;
		}

		const scrollY = window.scrollY;
		const isScrollingBack = scrollY < lastScrollY - 1;

		setBodyScrollBack( isScrollingBack );
		lastScrollY = scrollY;
	}

	function headerTriggerY() {
		return header.getBoundingClientRect().bottom;
	}

	function reserveHomePlaceholder() {
		placeholder.classList.add( 'is-active' );
		placeholder.style.width = homeMetrics.width + 'px';
		placeholder.style.height = homeMetrics.height + 'px';
	}

	function getHeaderLogoTarget( startRect ) {
		const logoBox = document.querySelector( 'header .logo' );
		const boxRect = logoBox ? logoBox.getBoundingClientRect() : headerLogoLink.getBoundingClientRect();
		const maxWidth = boxRect.width || 196;
		const maxHeight = 58;
		const aspect = startRect.width / startRect.height;

		let width = maxWidth;
		let height = width / aspect;

		if ( height > maxHeight ) {
			height = maxHeight;
			width = height * aspect;
		}

		return {
			top: boxRect.top + ( maxHeight - height ) / 2,
			left: boxRect.left,
			width: width,
			height: height,
			scaleX: width / startRect.width,
			scaleY: height / startRect.height,
		};
	}

	function pinToViewport( rect ) {
		bannerLogo.style.position = 'fixed';
		bannerLogo.style.top = rect.top + 'px';
		bannerLogo.style.left = rect.left + 'px';
		bannerLogo.style.width = rect.width + 'px';
		bannerLogo.style.height = rect.height + 'px';
		bannerLogo.style.zIndex = '1002';
		bannerLogo.style.margin = '0';
		bannerLogo.style.transformOrigin = '0 0';
		bannerLogo.style.opacity = '1';
	}

	function settleAtTarget( target ) {
		bannerLogo.style.transition = 'none';
		bannerLogo.style.transform = 'none';
		bannerLogo.style.top = target.top + 'px';
		bannerLogo.style.left = target.left + 'px';
		bannerLogo.style.width = target.width + 'px';
		bannerLogo.style.height = target.height + 'px';
		void bannerLogo.offsetHeight;
	}

	function runDockAnimation( startRect, target, onComplete ) {
		const translateX = target.left - startRect.left;
		const translateY = target.top - startRect.top;

		document.body.appendChild( bannerLogo );
		pinToViewport( startRect );
		bannerLogo.classList.add( 'banner-logo--animating' );
		bannerLogo.classList.remove( 'banner-logo--docked', 'banner-logo--scroll' );
		bannerLogo.style.transition = BANNER_LOGO_TRANSITION;
		bannerLogo.style.transform = 'translate(0, 0) scale(1)';

		requestAnimationFrame( function () {
			requestAnimationFrame( function () {
				bannerLogo.style.transform =
					'translate(' +
					translateX +
					'px, ' +
					translateY +
					'px) scale(' +
					target.scaleX +
					', ' +
					target.scaleY +
					')';
			} );
		} );

		let finished = false;

		function finishAnimation() {
			if ( finished ) {
				return;
			}
			finished = true;

			bannerLogo.removeEventListener( 'transitionend', onTransitionEnd );
			window.clearTimeout( fallbackTimer );
			bannerLogo.classList.remove( 'banner-logo--animating' );
			settleAtTarget( target );

			if ( typeof onComplete === 'function' ) {
				onComplete();
			}
		}

		function onTransitionEnd( event ) {
			if ( event.propertyName !== 'transform' ) {
				return;
			}
			finishAnimation();
		}

		const fallbackTimer = window.setTimeout( finishAnimation, 600 );

		bannerLogo.addEventListener( 'transitionend', onTransitionEnd );
	}

	function completeDock() {
		headerLogoLink.appendChild( bannerLogo );
		clearPositionStyles( bannerLogo );
		bannerLogo.classList.add( 'banner-logo--docked' );
		setBodyDocked( true );
		setBodyScrollBack( false );
		reserveHomePlaceholder();
		state = 'docked';
	}

	function resetHeaderDefaultLogo() {
		if ( ! headerDefaultImg ) {
			return;
		}

		headerDefaultImg.style.opacity = '';
		headerDefaultImg.style.height = '';
	}

	function resetAnimationState() {
		if ( bannerLogo.parentElement !== bannerLogoWrap ) {
			bannerLogoWrap.appendChild( bannerLogo );
		}

		clearPositionStyles( bannerLogo );
		bannerLogo.classList.remove(
			'banner-logo--animating',
			'banner-logo--docked',
			'banner-logo--scroll'
		);
		setBodyDocked( false );
		setBodyScrollBack( false );
		placeholder.classList.remove( 'is-active' );
		placeholder.style.width = '';
		placeholder.style.height = '';
		resetHeaderDefaultLogo();
		state = 'idle';
		homeMetrics = captureHomeMetrics();
		lastScrollY = window.scrollY;
	}

	function dockLogo() {
		if ( ! isAnimationEnabled() || state === 'animating' || state === 'docked' ) {
			return;
		}

		const startRect = bannerLogo.getBoundingClientRect();
		const target = getHeaderLogoTarget( startRect );
		state = 'animating';

		setBodyScrollBack( false );
		reserveHomePlaceholder();

		if ( headerDefaultImg ) {
			headerDefaultImg.style.opacity = '0';
			headerDefaultImg.style.height = '0';
		}

		bannerLogo.classList.remove( 'banner-logo--scroll' );

		runDockAnimation( startRect, target, completeDock );
	}

	function updateLogoOnScroll() {
		if ( ! isAnimationEnabled() ) {
			resetAnimationState();
			return;
		}

		updateScrollBackClass();

		if ( state === 'animating' ) {
			return;
		}

		if ( state === 'docked' ) {
			return;
		}

		const triggerY = headerTriggerY();
		const logoRect = bannerLogo.getBoundingClientRect();
		const reachedHeaderTop = logoRect.top <= triggerY;

		if ( reachedHeaderTop ) {
			dockLogo();
			return;
		}

		if ( logoRect.top <= triggerY + 100 ) {
			state = 'scroll';
			bannerLogo.classList.add( 'banner-logo--scroll' );
		} else {
			bannerLogo.classList.remove( 'banner-logo--scroll' );
			state = 'idle';
		}
	}

	function handleViewportChange() {
		if ( isAnimationEnabled() ) {
			updateLogoOnScroll();
			return;
		}

		resetAnimationState();
	}

	updateLogoOnScroll();
	window.addEventListener( 'scroll', updateLogoOnScroll, { passive: true } );

	if ( typeof animationMediaQuery.addEventListener === 'function' ) {
		animationMediaQuery.addEventListener( 'change', handleViewportChange );
	} else if ( typeof animationMediaQuery.addListener === 'function' ) {
		animationMediaQuery.addListener( handleViewportChange );
	}
}

// Banner Animation
export function initBannerAnimation() {
	const GROUPS = [".h-flower", ".l-flower", ".h-vine", ".l-vine"];
	const CLOUDS = [
	  ".left-cloud-1",
	  ".left-cloud-2",
	  ".right-cloud-1",
	  ".right-cloud-2",
	  ".right-cloud-3",
	];
	const SVG_NS = "http://www.w3.org/2000/svg";
	const BUTTERFLIES = [
	  {
		name: "violet-head",
		classes: ["banner-cls-20"],
		box: { x1: 171, y1: 228, x2: 186, y2: 241 },
		from: { x: -18, y: -9, r: -10 },
		delay: 1.45,
		flap: 0.18,
	  },
	  {
		name: "pink-left-cloud",
		classes: ["banner-cls-82"],
		box: { x1: 97, y1: 270, x2: 112, y2: 282 },
		from: { x: -24, y: 10, r: 8 },
		delay: 1.6,
		flap: 0.2,
	  },
	  {
		name: "blue-left-flower",
		classes: ["banner-cls-6"],
		box: { x1: 54, y1: 333, x2: 69, y2: 345 },
		from: { x: -18, y: 16, r: -6 },
		delay: 1.78,
		flap: 0.19,
	  },
	  {
		name: "pink-right-flower",
		classes: ["banner-cls-82"],
		box: { x1: 390, y1: 336, x2: 405, y2: 348 },
		from: { x: 22, y: -12, r: 8 },
		delay: 1.7,
		flap: 0.21,
	  },
	  {
		name: "orange-right-flower",
		classes: ["banner-cls-29"],
		box: { x1: 450, y1: 339, x2: 466, y2: 352 },
		from: { x: 24, y: 12, r: -8 },
		delay: 1.9,
		flap: 0.18,
	  },
	  {
		name: "blue-right-flower",
		classes: ["banner-cls-6"],
		box: { x1: 418, y1: 355, x2: 433, y2: 368 },
		from: { x: 18, y: 18, r: 7 },
		delay: 2.05,
		flap: 0.2,
	  },
	];
	const CLOCKS = [
	  {
		name: "small",
		box: { x1: 157, y1: 570, x2: 184, y2: 597 },
		center: { x: 171.36, y: 583.16 },
		radius: 11.9,
		mode: "intro",
		sweepDuration: 1.35,
		minuteHand: "largest",
	  },
	  {
		name: "center",
		box: { x1: 230, y1: 581, x2: 274, y2: 626 },
		center: { x: 251.98, y: 603.61 },
		radius: 20.2,
		mode: "local",
	  },
	  {
		name: "local",
		box: { x1: 351, y1: 555, x2: 390, y2: 613 },
		center: { x: 370.98, y: 575.67 },
		radius: 17,
		mode: "intro",
		sweepDuration: 1.35,
		minuteHand: "largest",
		legacyHandBoxes: [
		  { x: 363.58, y: 570.12, width: 7.1, height: 5.4 },
		  { x: 371.88, y: 576.62, width: 7.22, height: 9.82 },
		],
	  },
	];
	
	function setOp(list, op) {
	  list.forEach(function (s) {
		var e = document.querySelector(s);
		if (e) {
		  e.style.opacity = op;
		}
	  });
	}
	
	function setGroups(op) {
	  setOp(GROUPS, op);
	}
	
	function getBannerSvg() {
	  return (
		document.getElementById("Banner_Layer") ||
		document.querySelector(".bts-banner__media svg") ||
		document.querySelector("svg")
	  );
	}
	
	function makeSvgEl(name, attrs) {
	  var el = document.createElementNS(SVG_NS, name);
	  Object.keys(attrs || {}).forEach(function (key) {
		el.setAttribute(key, attrs[key]);
	  });
	  return el;
	}
	
	function safeBox(el) {
	  try {
		var b = el.getBBox();
		return b && b.width >= 0 ? b : null;
	  } catch (e) {
		return null;
	  }
	}
  
	function boxMatchesTarget(box, target) {
	  var tolerance = 0.65;
	  return (
		Math.abs(box.x - target.x) <= tolerance &&
		Math.abs(box.y - target.y) <= tolerance &&
		Math.abs(box.width - target.width) <= tolerance &&
		Math.abs(box.height - target.height) <= tolerance
	  );
	}
	
	function boxCenter(b) {
	  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
	}
	
	function inBox(point, box) {
	  return (
		point.x >= box.x1 &&
		point.x <= box.x2 &&
		point.y >= box.y1 &&
		point.y <= box.y2
	  );
	}
	
	function sortByDocumentOrder(items) {
	  return items.sort(function (a, b) {
		if (a === b) {
		  return 0;
		}
		return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING
		  ? 1
		  : -1;
	  });
	}
	
	function collectLeafParts(svg, cfg) {
	  var allowed = cfg.classes || null;
	  var maxPart = cfg.maxPart || 42;
	  return sortByDocumentOrder(
		Array.prototype.slice
		  .call(svg.querySelectorAll("path,circle,ellipse,polygon,rect"))
		  .filter(function (el) {
			if (el.closest(".heal-semantic-group") || el.classList.contains("banner-cls-1")) {
			  return false;
			}
			if (allowed && allowed.indexOf(el.getAttribute("class") || "") === -1) {
			  return false;
			}
			var b = safeBox(el);
			if (!b || b.width > maxPart || b.height > maxPart) {
			  return false;
			}
			return inBox(boxCenter(b), cfg.box);
		  })
	  );
	}
	
	function wrapParts(parts, className) {
	  if (!parts.length) {
		return null;
	  }
	  var parent = parts[0].parentNode;
	  var group = makeSvgEl("g", { class: className + " heal-semantic-group" });
	  group.setAttribute("data-heal-generated", "true");
	  parent.insertBefore(group, parts[0]);
	  parts.forEach(function (part) {
		group.appendChild(part);
	  });
	  return group;
	}
	
	function nestExistingChildren(group, className) {
	  var inner = makeSvgEl("g", { class: className });
	  while (group.firstChild) {
		inner.appendChild(group.firstChild);
	  }
	  group.appendChild(inner);
	  return inner;
	}
	
	function initAlphaDrawAnimation(gsap) {
	  const paths = document.querySelectorAll(".alpha");
	
	  if (!paths.length) {
		console.warn("No .alpha paths found");
		return;
	  }
	
	  paths.forEach((path) => {
		const length = path.getTotalLength();
		gsap.set(path, {
		  strokeDasharray: length,
		  strokeDashoffset: length,
		  fillOpacity: 0,
		});
	  });
	
	  gsap.set(".a-flower-right", { x: -30, opacity: 0 });
	  gsap.set(".a-flower-left", { x: 30, opacity: 0 });
	  gsap.set(".e-flower-1", { x: -30, opacity: 0 });
	  gsap.set(".e-flower-2", { x: -30, opacity: 0 });
	
	  const tl = gsap.timeline();
	
	  tl.to(
		paths,
		{
		  strokeDashoffset: 0,
		  duration: 0.8,
		  ease: "power2.out",
		},
		0
	  )
		.to(
		  paths,
		  {
			fillOpacity: 1,
			strokeOpacity: 0,
			duration: 0.4,
			ease: "power1.out",
		  },
		  "-=0.2"
		)
		.to(
		  [".a-flower-right", ".a-flower-left", ".e-flower-1", ".e-flower-2"],
		  {
			x: 0,
			opacity: 1,
			duration: 0.8,
			ease: "back.out(1.7)",
			stagger: 0.05,
		  }
		);
	}
	
	function initOrganicGrowthAnimation(gsap) {
	  var svg =
		document.getElementById("Banner_Layer") ||
		document.querySelector(".bts-banner__media svg");
	  if (!svg) {
		return;
	  }
	
	  function q(sel, root) {
		return (root || svg).querySelector(sel);
	  }
	  function qa(sel, root) {
		return Array.prototype.slice.call((root || svg).querySelectorAll(sel));
	  }
	  function bb(el) {
		try {
		  var b = el.getBBox();
		  return b && b.width >= 0 ? b : null;
		} catch (e) {
		  return null;
		}
	  }
	  function cx(b) {
		return b.x + b.width / 2;
	  }
	  function cy(b) {
		return b.y + b.height / 2;
	  }
	  function clamp(v, lo, hi) {
		return v < lo ? lo : v > hi ? hi : v;
	  }
	
	  var ns = "http://www.w3.org/2000/svg";
	
	  var defs =
		svg.querySelector("defs") ||
		svg.insertBefore(document.createElementNS(ns, "defs"), svg.firstChild);
	  var uid = 0;
	
	  function makeReveal(el, padTop) {
		var b = bb(el);
		if (!b) {
		  return null;
		}
		var pad = 1.5;
		var top = padTop == null ? pad : padTop;
		var id = "healReveal" + uid++;
		var cp = document.createElementNS(ns, "clipPath");
		cp.setAttribute("id", id);
		cp.setAttribute("clipPathUnits", "userSpaceOnUse");
		var rect = document.createElementNS(ns, "rect");
		rect.setAttribute("x", b.x - pad);
		rect.setAttribute("y", b.y - top);
		rect.setAttribute("width", b.width + pad * 2);
		rect.setAttribute("height", b.height + top + pad);
		cp.appendChild(rect);
		defs.appendChild(cp);
		el.setAttribute("clip-path", "url(#" + id + ")");
		gsap.set(rect, {
		  svgOrigin: cx(b) + " " + (b.y + b.height + pad),
		  scaleY: 0.0001,
		});
		return rect;
	  }
	
	  function clearReveal(el) {
		el.removeAttribute("clip-path");
	  }
	
	  function prepLeaf(el, headX, headY) {
		var b = bb(el);
		if (!b) {
		  return null;
		}
		var ox = clamp(headX, b.x, b.x + b.width);
		var oy = clamp(headY, b.y, b.y + b.height);
		gsap.set(el, {
		  svgOrigin: ox + " " + oy,
		  opacity: 0,
		  scale: 0,
		  rotation: -10,
		  transformOrigin: "50% 50%",
		});
		return { el: el, my: cy(b) };
	  }
	
	  function playLeaf(tl, el, at) {
		tl.to(
		  el,
		  {
			keyframes: [
			  {
				opacity: 1,
				scale: 1.12,
				rotation: 2,
				duration: 0.2,
				ease: "power3.out",
			  },
			  { scale: 1, rotation: 0, duration: 0.14, ease: "power1.inOut" },
			],
		  },
		  at
		);
	  }
	
	  function prepBloom(group) {
		var gb = bb(group);
		if (!gb) {
		  return null;
		}
		var ctr = { x: cx(gb), y: cy(gb) };
		var petals = qa("path", group).map(function (p) {
		  var pb = bb(p);
		  var d = pb ? Math.hypot(cx(pb) - ctr.x, cy(pb) - ctr.y) : 0;
		  return { el: p, d: d };
		});
		var max =
		  petals.reduce(function (m, p) {
			return Math.max(m, p.d);
		  }, 0) || 1;
		petals.forEach(function (p) {
		  p.t = p.d / max;
		  gsap.set(p.el, {
			svgOrigin: ctr.x + " " + ctr.y,
			opacity: 0,
			scale: 0.2,
			rotation: -12,
			transformOrigin: "50% 50%",
		  });
		});
		return petals;
	  }
	
	  function playBloom(tl, petals, at, spread) {
		petals.forEach(function (p) {
		  tl.to(
			p.el,
			{
			  keyframes: [
				{
				  opacity: 1,
				  scale: 1.08,
				  rotation: 2,
				  duration: 0.22,
				  ease: "power3.out",
				},
				{ scale: 1, rotation: 0, duration: 0.16, ease: "power1.inOut" },
			  ],
			},
			at + p.t * spread
		  );
		});
	  }
	
	  var HEAD = { x: 250, y: 150 };
	
	  function buildSide(cfg, tl, t0) {
		var lowerStem = q(cfg.lowerStem);
		var upperStems = cfg.upperStems
		  .map(function (s) {
			return q(s);
		  })
		  .filter(Boolean);
	
		var lowerRect = makeReveal(lowerStem);
		var upperRects = upperStems.map(function (e) {
		  return makeReveal(e);
		});
	
		var leaves = cfg.leaves
		  .map(function (s) {
			return prepLeaf(q(s), HEAD.x, HEAD.y);
		  })
		  .filter(Boolean);
		var stemB = lowerStem ? bb(lowerStem) : null;
		leaves.forEach(function (lf) {
		  lf.rel = stemB
			? clamp((stemB.y + stemB.height - lf.my) / stemB.height, 0, 1)
			: 0.5;
		});
	
		var petals = prepBloom(q(cfg.bloom));
	
		var T_STEM = 0.62;
		var T_UP = 0.46;
	
		if (lowerRect) {
		  tl.to(
			lowerRect,
			{
			  scaleY: 1,
			  duration: T_STEM,
			  ease: "power2.out",
			  onComplete: function () {
				clearReveal(lowerStem);
			  },
			},
			t0
		  );
		}
	
		leaves.forEach(function (lf) {
		  playLeaf(tl, lf.el, t0 + 0.1 + lf.rel * (T_STEM * 0.82));
		});
	
		var tUp = t0 + T_STEM * 0.8;
		upperRects.forEach(function (r, i) {
		  if (!r) {
			return;
		  }
		  tl.to(
			r,
			{
			  scaleY: 1,
			  duration: T_UP,
			  ease: "power2.out",
			  onComplete: (function (el) {
				return function () {
				  clearReveal(el);
				};
			  })(upperStems[i]),
			},
			tUp
		  );
		});
	
		var tBloom = tUp + T_UP * 0.72;
		if (petals) {
		  playBloom(tl, petals, tBloom, 0.4);
		}
	
		return tBloom + 0.4 + 0.38;
	  }
	
	  function buildClouds(tl) {
		var clouds = [
		  { sel: ".left-cloud-2", from: -235, at: 0.0, dur: 1.55 },
		  { sel: ".left-cloud-1", from: -165, at: 0.16, dur: 1.45 },
		  { sel: ".right-cloud-3", from: 220, at: 0.04, dur: 1.6 },
		  { sel: ".right-cloud-2", from: 155, at: 0.18, dur: 1.45 },
		  { sel: ".right-cloud-1", from: 200, at: 0.3, dur: 1.5 },
		];
		clouds.forEach(function (c) {
		  var el = q(c.sel);
		  if (!el) {
			return;
		  }
		  gsap.set(el, { x: c.from, opacity: 0 });
		  tl.to(el, { x: 0, opacity: 1, duration: c.dur, ease: "power2.out" }, c.at);
		  var driftX = (c.from > 0 ? -1 : 1) * (5 + Math.random() * 3);
		  gsap.to(el, {
			x: "+=" + driftX,
			y: "-=" + (3 + Math.random() * 2),
			duration: 4.5 + Math.random() * 2.5,
			ease: "sine.inOut",
			repeat: -1,
			yoyo: true,
			delay: 1.9,
		  });
		});
	
		var glitters = [
		  ".left-cloud-1-glitter-1",
		  ".left-cloud-1-glitter-2",
		  ".left-cloud-2-glitter-1",
		  ".right-cloud-1-glitter-1",
		  ".right-cloud-1-glitter-2",
		  ".right-cloud-2-glitter-1",
		  ".right-cloud-2-glitter-2",
		  ".right-cloud-3-glitter-1",
		  ".right-cloud-3-glitter-2",
		];
		glitters.forEach(function (s, i) {
		  var el = q(s);
		  if (!el) {
			return;
		  }
		  var b = bb(el);
		  if (!b) {
			return;
		  }
		  gsap.set(el, {
			svgOrigin: cx(b) + " " + cy(b),
			transformOrigin: "50% 50%",
		  });
		  gsap.to(el, {
			keyframes: [
			  { scale: 1.5, opacity: 1, duration: 0.22, ease: "power2.out" },
			  { scale: 0.65, opacity: 0.4, duration: 0.18, ease: "power2.in" },
			  { scale: 1.2, opacity: 0.95, duration: 0.16, ease: "power2.out" },
			  { scale: 1, opacity: 0.85, duration: 0.14 },
			  {
				scale: 1,
				opacity: 0.85,
				duration: 0.9 + Math.random() * 1.1,
			  },
			],
			repeat: -1,
			delay: 1.6 + i * 0.17,
		  });
		});
	  }
	
	  var tl = gsap.timeline({ defaults: { ease: "power2.out" } });
	  window.__healTL = tl;
	
	  buildClouds(tl);
	
	  var STEM_START = 0.14;
	  buildSide(
		{
		  lowerStem: ".h-stem-1",
		  upperStems: [".h-flower-vine-content"],
		  leaves: [".h-leaf-1", ".h-leaf-2", ".h-leaf-3", ".h-leaf-4"],
		  bloom: ".h-flower-bloom-content",
		},
		tl,
		STEM_START
	  );
	
	  buildSide(
		{
		  lowerStem: ".l-stem-1",
		  upperStems: [".l-flower-vine-shape", ".l-flower-vine-fill"],
		  leaves: [
			".l-leaf-1",
			".l-leaf-2",
			".l-leaf-3",
			".l-leaf-4",
			".l-flower-leaves-fill",
		  ],
		  bloom: ".l-flower-bloom-content",
		},
		tl,
		STEM_START
	  );
	
	  setGroups("1");
	}
	
	function initButterflyAnimations(gsap) {
	  var svg = getBannerSvg();
	  if (!svg) {
		return;
	  }
	
	  var groups = [];
	
	  BUTTERFLIES.forEach(function (cfg, i) {
		var parts = collectLeafParts(svg, {
		  box: cfg.box,
		  classes: cfg.classes,
		  maxPart: 18,
		});
		var outer = wrapParts(parts, "heal-butterfly heal-butterfly-" + cfg.name);
		if (!outer) {
		  return;
		}
	
		var flutter = nestExistingChildren(outer, "heal-butterfly__flutter");
		var b = safeBox(flutter);
		if (!b) {
		  return;
		}
		var c = boxCenter(b);
		var settleDelay = cfg.delay + 1.7 + i * 0.05;
	
		gsap.set(outer, {
		  svgOrigin: c.x + " " + c.y,
		  opacity: 0,
		  x: cfg.from.x,
		  y: cfg.from.y,
		  rotation: cfg.from.r,
		  scale: 0.88,
		  transformOrigin: "50% 50%",
		});
		gsap.set(flutter, {
		  svgOrigin: c.x + " " + c.y,
		  transformOrigin: "50% 50%",
		});
	
		gsap
		  .timeline({ delay: cfg.delay })
		  .to(outer, { opacity: 1, duration: 0.18, ease: "power1.out" })
		  .to(outer, {
			keyframes: [
			  {
				x: cfg.from.x * 0.48,
				y: cfg.from.y * 0.18 - 4,
				rotation: cfg.from.r * -0.4,
				scale: 1.04,
				duration: 0.68,
				ease: "sine.inOut",
			  },
			  {
				x: 0,
				y: 0,
				rotation: 0,
				scale: 1,
				duration: 0.9,
				ease: "back.out(1.4)",
			  },
			],
		  });
	
		gsap.to(flutter, {
		  scaleX: 0.62,
		  scaleY: 1.08,
		  rotation: i % 2 ? 2 : -2,
		  duration: cfg.flap,
		  ease: "sine.inOut",
		  repeat: -1,
		  yoyo: true,
		  delay: cfg.delay * 0.45,
		});
		gsap.to(outer, {
		  y: "-=1.25",
		  rotation: i % 2 ? "+=1.4" : "-=1.4",
		  duration: 2.4 + i * 0.18,
		  ease: "sine.inOut",
		  repeat: -1,
		  yoyo: true,
		  delay: settleDelay,
		});
	
		groups.push(outer);
	  });
	
	  window.__healButterflies = groups;
	}
	
	function roundCoord(value) {
	  return Math.round(value * 100) / 100;
	}
	
	function createPointedClockHandPath(center, length, width) {
	  var cx = center.x;
	  var cy = center.y;
	  var tipY = cy - length;
	  var headLength = Math.min(length * 0.32, Math.max(width * 1.4, 1.55));
	  var neckY = tipY + headLength;
	  var shaftHalf = Math.max(width * 0.2, 0.28);
	  var headHalf = Math.max(width * 0.62, 0.72);
	  var tailY = cy + Math.max(width * 0.28, 0.36);
	
	  return [
		"M",
		roundCoord(cx),
		roundCoord(tipY),
		"C",
		roundCoord(cx + headHalf * 0.9),
		roundCoord(tipY + headLength * 0.22),
		roundCoord(cx + headHalf),
		roundCoord(neckY - headLength * 0.18),
		roundCoord(cx + shaftHalf),
		roundCoord(neckY),
		"L",
		roundCoord(cx + shaftHalf),
		roundCoord(tailY),
		"L",
		roundCoord(cx - shaftHalf),
		roundCoord(tailY),
		"L",
		roundCoord(cx - shaftHalf),
		roundCoord(neckY),
		"C",
		roundCoord(cx - headHalf),
		roundCoord(neckY - headLength * 0.18),
		roundCoord(cx - headHalf * 0.9),
		roundCoord(tipY + headLength * 0.22),
		roundCoord(cx),
		roundCoord(tipY),
		"Z",
	  ].join(" ");
	}
	
	function createClockHand(center, length, width, className, color, pointed) {
	  if (pointed) {
		return makeSvgEl("path", {
		  class: "heal-clock-hand " + className,
		  d: createPointedClockHandPath(center, length, width),
		  fill: color || "#050608",
		  stroke: color || "#050608",
		  "stroke-width": Math.max(width * 0.16, 0.18),
		  "stroke-linejoin": "round",
		  "vector-effect": "non-scaling-stroke",
		});
	  }
	
	  return makeSvgEl("line", {
		class: "heal-clock-hand " + className,
		x1: center.x,
		y1: center.y,
		x2: center.x,
		y2: center.y - length,
		stroke: color || "#050608",
		"stroke-width": width,
		"stroke-linecap": "round",
		"vector-effect": "non-scaling-stroke",
	  });
	}
	
	function hideLegacyClockHands(gsap, group, cfg) {
	  var center = cfg.center;
	  var targets = cfg.legacyHandBoxes || [];
	
	  Array.prototype.slice.call(group.querySelectorAll("path")).forEach(function (el) {
		var b = safeBox(el);
		if (!b) {
		  return;
		}
		if (targets.length) {
		  if (
			targets.some(function (target) {
			  return boxMatchesTarget(b, target);
			})
		  ) {
			gsap.set(el, { opacity: 0 });
		  }
		  return;
		}
		var crossesCenter =
		  b.x <= center.x + 1 &&
		  b.x + b.width >= center.x - 1 &&
		  b.y <= center.y + 1 &&
		  b.y + b.height >= center.y - 1;
		var elongated = Math.max(b.width, b.height) > cfg.radius * 0.22;
		var compact = b.width < cfg.radius * 1.2 && b.height < cfg.radius * 1.2;
		if (crossesCenter && compact && elongated) {
		  gsap.set(el, { opacity: 0 });
		}
	  });
	}
  
	function findOriginalClockHands(group, cfg) {
	  var center = cfg.center;
	  var targets = cfg.legacyHandBoxes || [];
	  var matches = [];
  
	  Array.prototype.slice.call(group.querySelectorAll("path")).forEach(function (el) {
		var b = safeBox(el);
		if (!b) {
		  return;
		}
  
		if (targets.length) {
		  targets.forEach(function (target, index) {
			if (boxMatchesTarget(b, target)) {
			  matches[index] = el;
			}
		  });
		  return;
		}
  
		var crossesCenter =
		  b.x <= center.x + 1 &&
		  b.x + b.width >= center.x - 1 &&
		  b.y <= center.y + 1 &&
		  b.y + b.height >= center.y - 1;
		var elongated = Math.max(b.width, b.height) > cfg.radius * 0.22;
		var compact = b.width < cfg.radius * 1.2 && b.height < cfg.radius * 1.2;
		if (crossesCenter && compact && elongated) {
		  matches.push(el);
		}
	  });
  
	  return matches.filter(Boolean);
	}
	
	function createClockHands(gsap, group, cfg) {
	  var center = cfg.center;
	  var radius = cfg.radius;
	  var hands = makeSvgEl("g", {
		class: "heal-clock-hands heal-clock-hands-" + cfg.name,
	  });
	  var hour = createClockHand(
		center,
		radius * 0.43,
		Math.max(radius * 0.085, 1.1),
		"heal-clock-hour",
		"#050608",
		true
	  );
	  var minute = createClockHand(
		center,
		radius * 0.61,
		Math.max(radius * 0.065, 0.85),
		"heal-clock-minute",
		"#050608",
		true
	  );
	  var second = createClockHand(
		center,
		radius * 0.74,
		0.55,
		"heal-clock-second",
		"#e6174f"
	  );
	  var pin = makeSvgEl("circle", {
		class: "heal-clock-pin",
		cx: center.x,
		cy: center.y,
		r: Math.max(0.8, radius * 0.09),
		fill: "#050608",
	  });
	
	  hands.style.pointerEvents = "none";
	  hands.appendChild(hour);
	  hands.appendChild(minute);
	  hands.appendChild(second);
	  hands.appendChild(pin);
	  group.appendChild(hands);
	
	  gsap.set([hour, minute, second], {
		svgOrigin: center.x + " " + center.y,
		transformOrigin: "50% 100%",
	  });
	
	  return { hour: hour, minute: minute, second: second, pin: pin };
	}
	
	function getLargerClockHand(a, b) {
	  var boxA = safeBox(a);
	  var boxB = safeBox(b);
	  var sizeA = boxA ? Math.max(boxA.width, boxA.height) : 0;
	  var sizeB = boxB ? Math.max(boxB.width, boxB.height) : 0;
	  return sizeB > sizeA ? b : a;
	}
  
	function animateIntroClockSweep(gsap, cfg, group) {
	  var originalHands = findOriginalClockHands(group, cfg);
	  if (!originalHands.length) {
		return null;
	  }
  
	  var minuteHand =
		cfg.minuteHand === "largest"
		  ? originalHands.reduce(getLargerClockHand)
		  : originalHands[originalHands.length - 1];
  
	  gsap.set(originalHands, { opacity: 1 });
	  gsap.set(minuteHand, {
		rotation: 0,
		svgOrigin: cfg.center.x + " " + cfg.center.y,
		transformOrigin: "50% 50%",
	  });
	  gsap.to(minuteHand, {
		rotation: "+=360",
		duration: cfg.sweepDuration || 1.35,
		ease: "power2.inOut",
		onComplete: function () {
		  gsap.set(minuteHand, { rotation: 0 });
		},
	  });
  
	  return { originalHands: originalHands, minute: minuteHand };
	}
	
	function syncLocalClock(gsap, hands) {
	  var now = new Date();
	  var seconds = now.getSeconds() + now.getMilliseconds() / 1000;
	  var minutes = now.getMinutes() + seconds / 60;
	  var hours = (now.getHours() % 12) + minutes / 60;
	  gsap.set(hands.second, { rotation: seconds * 6 });
	  gsap.set(hands.minute, { rotation: minutes * 6 });
	  gsap.set(hands.hour, { rotation: hours * 30 });
	}
	
	function startLocalClock(gsap, hands) {
	  function tick() {
		syncLocalClock(gsap, hands);
		window.__healClockFrame = requestAnimationFrame(tick);
	  }
	
	  tick();
	}
	
	function initClockAnimations(gsap) {
	  var svg = getBannerSvg();
	  if (!svg) {
		return;
	  }
	
	  if (window.__healClockTimer) {
		clearInterval(window.__healClockTimer);
		window.__healClockTimer = null;
	  }
	  if (window.__healClockFrame) {
		cancelAnimationFrame(window.__healClockFrame);
		window.__healClockFrame = null;
	  }
	
	  var clocks = [];
	  CLOCKS.forEach(function (cfg) {
		var parts = collectLeafParts(svg, {
		  box: cfg.box,
		  classes: ["", "banner-cls-8", "banner-cls-75", "banner-cls-85", "banner-cls-86"],
		  maxPart: cfg.name === "local" ? 64 : 46,
		});
		var group = wrapParts(parts, "heal-clock heal-clock-" + cfg.name);
		if (!group) {
		  return;
		}
	
		var hands = null;
		if (group.parentNode) {
		  group.parentNode.appendChild(group);
		}
		if (cfg.mode === "local") {
		  hideLegacyClockHands(gsap, group, cfg);
		  hands = createClockHands(gsap, group, cfg);
		  startLocalClock(gsap, hands);
		} else {
		  hands = animateIntroClockSweep(gsap, cfg, group);
		}
	
		clocks.push({ group: group, hands: hands, config: cfg });
	  });
	
	  window.__healClocks = clocks;
	}
	
	function loadGsap(onReady) {
	  if (window.gsap) {
		onReady(window.gsap);
		return;
	  }
	
	  var script = document.createElement("script");
	  script.src =
		"https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js";
	  script.onload = function () {
		onReady(window.gsap);
	  };
	  script.onerror = function () {
		setGroups("1");
		setOp(CLOUDS, "1");
	revealBanner();
	  };
	  document.head.appendChild(script);
	}
  
  function getBannerLayer() {
	return document.getElementById("Banner_Layer");
  }
  
  function revealBanner() {
	var layer = getBannerLayer();
	if (!layer) {
	  return;
	}
	layer.style.transition = "opacity 0.9s ease";
	layer.style.willChange = "opacity";
	requestAnimationFrame(function () {
	  requestAnimationFrame(function () {
	  layer.style.opacity = "1";
	  });
	});
	window.setTimeout(function () {
	  layer.style.willChange = "";
	  layer.style.transition = "";
	}, 1100);
  }
  
	function applyResponsiveSvgHeight() {
	  var svg = getBannerSvg();
	  if (!svg) {
		return;
	  }
	  if (window.innerWidth < 720) {
		svg.style.height = "400px";
	  } else {
		svg.style.height = "";
	  }
	}
  
	function startBannerAnimation() {
	  loadGsap(function (gsap) {
		if (!gsap) {
		  return;
		}
		initAlphaDrawAnimation(gsap);
		initOrganicGrowthAnimation(gsap);
		initButterflyAnimations(gsap);
		initClockAnimations(gsap);
	revealBanner();
	  });
	}
	
	try {
	  setGroups("0");
	  setOp(CLOUDS, "0");
	} catch (e) {}
  
	applyResponsiveSvgHeight();
	window.addEventListener("resize", applyResponsiveSvgHeight);
	
	if (document.readyState === "loading") {
	  document.addEventListener("DOMContentLoaded", startBannerAnimation);
	} else {
	  startBannerAnimation();
	}
}
  
// Homepage Image Reveal Animation
export function initImageRevealAnimation() {
	const section = document.querySelector(
	".bts-two-column-image-with-statistics"
	);

	if (!section) return;

	const imageWrap = section.querySelector(
	".bts-two-column-image-with-statistics__image-wrap"
	);

	if (!imageWrap) return;

	const observer = new IntersectionObserver(
	(entries, obs) => {
		entries.forEach((entry) => {
		if (!entry.isIntersecting) return;

		gsap.fromTo(
			imageWrap,
			{
			clipPath: "inset(0 0 100% 0)",
			rotateX: -20,
			scaleY: 1.05,
			transformOrigin: "top center",
			},
			{
			clipPath: "inset(0 0 0% 0)",
			rotateX: 0,
			scaleY: 1,
			duration: 1.5,
			ease: "power2.inOut",
			}
		);

		obs.disconnect();
		});
	},
	{ threshold: 0.2 }
	);

	observer.observe(section);
}
  
// Campaign animation
export function initCampaignImageAnimation() {
	const section = document.querySelector('.bts-engagement-cards');

	if (!section) {
		console.error('❌ Section .bts-engagement-cards NOT found');
		return;
	}

	if (typeof gsap === 'undefined') {
		console.error('❌ GSAP is NOT loaded');
		return;
	}

	const cards = [...section.querySelectorAll('.each-card')];

	if (cards.length < 3) {
		console.error('❌ Expected 3 .each-card elements');
		return;
	}

	// Initial hidden states
	gsap.set(cards[0], {
		clipPath: 'inset(0 0 100% 0)',
		rotateX: -20,
		scaleY: 1.05,
		transformOrigin: 'top center',
	});

	gsap.set(cards[1], {
		clipPath: 'inset(100% 0 0% 0)',
		rotateX: 20,
		scaleY: 1.05,
		transformOrigin: 'bottom center',
	});

	gsap.set(cards[2], {
		clipPath: 'inset(0 0 100% 0)',
		rotateX: -20,
		scaleY: 1.05,
		transformOrigin: 'top center',
	});

	let animated = false;

	const runAnimation = () => {
		if (animated) return;

		animated = true;

		const tl = gsap.timeline();

		tl.to(cards[0], {
			clipPath: 'inset(0 0 0% 0)',
			rotateX: 0,
			scaleY: 1,
			duration: 1.5,
			ease: 'power2.inOut',
		});

		tl.to(
			cards[1],
			{
				clipPath: 'inset(0% 0 0% 0)',
				rotateX: 0,
				scaleY: 1,
				duration: 1.5,
				ease: 'power2.inOut',
			},
			'<'
		);

		tl.to(
			cards[2],
			{
				clipPath: 'inset(0 0 0% 0)',
				rotateX: 0,
				scaleY: 1,
				duration: 1.5,
				ease: 'power2.inOut',
			},
			'<'
		);
	};

	const observer = new IntersectionObserver(
		(entries, obs) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				runAnimation();
				obs.disconnect();
			});
		},
		{
			threshold: 0.1,
		}
	);

	observer.observe(section);

	// Fallback if already visible
	const rect = section.getBoundingClientRect();

	if (rect.top < window.innerHeight && rect.bottom > 0) {
		runAnimation();
	}
}
  
// Bottom Lotus animation in Homepage
export function initBottomLotusAnimation() {
	const bottomSection = document.querySelector(
		'.bts-cards-with-image--bottom-image'
	);

	if (!bottomSection) return;

	const bottomLotus = bottomSection.querySelector('.lotus-image');

	if (!bottomLotus) return;

	let lastScrollY = window.scrollY;
	let hasBloomed = false;

	const bottomObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const scrollingDown = window.scrollY > lastScrollY;

				if (entry.isIntersecting) {
					hasBloomed = true;
					bottomLotus.classList.add('is-bloomed');
				} else if (hasBloomed && !scrollingDown) {
					hasBloomed = false;
					bottomLotus.classList.remove('is-bloomed');
				}

				lastScrollY = window.scrollY;
			});
		},
		{
			threshold: 0.5,
		}
	);

	bottomObserver.observe(bottomLotus);
}
  
// Footer flowers animation
export function initFooterFlowersAnimation() {
	const footer = document.querySelector('.bts-footer');

	if (!footer) return;

	let initialized = false;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting || initialized) return;

				initialized = true;

				const groups = [
					document.querySelectorAll('.floating-image-1'),
					document.querySelectorAll('.floating-image-2'),
					document.querySelectorAll('.floating-image-3'),
					document.querySelectorAll('.floating-image-4'),
					document.querySelectorAll('.floating-image-5'),
					document.querySelectorAll('.floating-image-6'),
				];

				groups.forEach((group, index) => {
					setTimeout(() => {
						group.forEach((el) => {
							el.classList.add('is-visible');
						});

						if (index === 1) {
							setTimeout(() => {
								group.forEach((el) =>
									el.classList.add('is-floating')
								);
							}, 500);
						}

						if (index === 2) {
							setTimeout(() => {
								group.forEach((el) =>
									el.classList.add('is-floating-reverse')
								);
							}, 500);
						}

						if (index === 4) {
							setTimeout(() => {
								group.forEach((el) =>
									el.classList.add('is-swaying')
								);
							}, 500);
						}
					}, index * 300);
				});
			});
		},
		{
			threshold: 0,
			rootMargin: '0px 0px -10% 0px',
		}
	);

	observer.observe(footer);
}
  
// Footer Flower animation
export function initFooterFlowerAnimation() {
	const section = document.querySelector('.bts-footer');
	const flower = document.querySelector('.footer-flower');

	if (!section || !flower) {
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.intersectionRatio >= 0.9) {
					flower.classList.add('is-bloomed');
				} else {
					flower.classList.remove('is-bloomed');
				}
			});
		},
		{
			threshold: [0, 0.9],
		}
	);

	observer.observe(section);
}
  
// Arrow Animation
export function initArrowAnimation() {
const arrows = document.querySelectorAll(".flexible-arrow");

if (!arrows.length) return;

arrows.forEach((arrow) => {
	// Reset state — remove class so it doesn't pre-animate
	arrow.classList.remove("is-animating");

	const observer = new IntersectionObserver(
	(entries, obs) => {
		const entry = entries[0];

		if (entry.isIntersecting) {
		arrow.classList.add("is-animating");
		obs.disconnect();
		}
	},
	{
		threshold: 0,
		// Fire only when arrow is 80px inside the viewport bottom
		// (i.e. user has scrolled close to it)
		rootMargin: "0px 0px -80px 0px",
	}
	);

	// Each arrow gets its own observer — no cross-contamination
	observer.observe(arrow);
});
}
  
// Voices page Lotus Animation
export function initVoicesLotusAnimation() {

	const section = document.querySelector('.artworks-section');
	const lotus = section?.querySelector('.lotus-image');

	if (!section || !lotus) {
		console.log('Voices Lotus Animation: elements not found');
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];

			if (entry.isIntersecting) {
				console.log('Voices Lotus Animation: bloom');
				lotus.classList.add('is-bloomed');
			} else {
				console.log('Voices Lotus Animation: reset');
				lotus.classList.remove('is-bloomed');
			}
		},
		{
			threshold: 0,
			rootMargin: '-80px 0px 0px 0px',
		}
	);
	observer.observe(section);
}
  
// Resources Page Lotus Animation
export function initResourcespageLotusAnimation() {
	const section = document.querySelector('.safe-circle-section');
	const lotus = section?.querySelector('.lotus-image');

	if (!section || !lotus) {
		console.log('Resources Page Lotus Animation: elements not found');
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];

			if (entry.intersectionRatio >= 0.2) {
				console.log('Resources Page Lotus Animation: bloom');
				lotus.classList.add('is-bloomed');
			} else {
				console.log('Resources Page Lotus Animation: reset');
				lotus.classList.remove('is-bloomed');
			}
		},
		{
			threshold: [0.2],
		}
	);

	observer.observe(section);
}
  
// How to join page Lotus Animation
export function initHowtojoinLotusAnimation() {
console.log('How To Join Lotus Animation: script loaded');

const section = document.querySelector('.bts-contact-columns');
const lotus = section?.querySelector('.lotus-image');

console.log('How To Join Lotus Animation: section:', section);
console.log('How To Join Lotus Animation: lotus:', lotus);

if (!section || !lotus) {
	console.log('How To Join Lotus Animation: elements not found');
	return;
}

lotus.classList.remove('is-bloomed');
console.log('How To Join Lotus Animation: is-bloomed removed');

const observer = new IntersectionObserver(
	(entries) => {
	const entry = entries[0];
	console.log('How To Join Lotus Animation: intersectionRatio:', entry.intersectionRatio);

	if (entry.intersectionRatio >= 0.2) {
		console.log('How To Join Lotus Animation: bloom');
		lotus.classList.add('is-bloomed');
	} else {
		console.log('How To Join Lotus Animation: reset');
		lotus.classList.remove('is-bloomed');
	}
	},
	{
	threshold: [0.2],
	}
);

observer.observe(section);
console.log('How To Join Lotus Animation: observer attached to section');
}

// How to join page artwork Lotus Animation
export function initArtworkFormSection() {
	const section = document.querySelector('.artwork-form-section');
	const lotus = section?.querySelector('.lotus-image');

	if (!section || !lotus) {
		console.log('Artwork Form Section Animation: elements not found');
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];

			if (entry.intersectionRatio >= 0.1) {
				console.log('Artwork Form Section Animation: bloom');
				lotus.classList.add('is-bloomed');
			} else {
				console.log('Artwork Form Section Animation: reset');
				lotus.classList.remove('is-bloomed');
			}
		},
		{
			threshold: 0.1,
			rootMargin: '0px 0px -20% 0px',
		}
	);

	observer.observe(section);
}

// Artwork card animation
export function initArtworksCardAnimation() {
	console.log('✅ initArtworksCardAnimation loaded');

	const section = document.querySelector('.bts-artworks');

	if (!section) {
		console.error('❌ Section .bts-artworks NOT found');
		return;
	}

	console.log('✅ .bts-artworks section found');

	if (typeof gsap === 'undefined') {
		console.error('❌ GSAP is NOT loaded');
		return;
	}

	console.log('✅ GSAP is loaded');

	const cards = [...section.querySelectorAll('.bts-artworks__card')];

	if (cards.length < 1) {
		console.error('❌ No .bts-artworks__card elements found');
		return;
	}

	console.log(`✅ Found ${cards.length} cards to animate`);

	// Set initial hidden states for all cards (the anchor elements)
	cards.forEach((card, index) => {
		gsap.set(card, {
			clipPath: 'inset(0 0 100% 0)',
			rotateX: -20,
			scaleY: 1.05,
			transformOrigin: 'top center',
		});
	});

	console.log('✅ Cards hidden states set');

	let animated = false;

	const runAnimation = () => {
		if (animated) return;

		animated = true;
		console.log('🎬 Animation started');

		const tl = gsap.timeline();

		// Animate each card with a slight stagger
		cards.forEach((card, index) => {
			tl.to(
				card,
				{
					clipPath: 'inset(0 0 0% 0)',
					rotateX: 0,
					scaleY: 1,
					duration: 1.5,
					ease: 'power2.inOut',
				},
				index * 0.15 // Stagger each card by 0.15 seconds
			);
		});
	};

	const observer = new IntersectionObserver(
		(entries, obs) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				console.log('👁️ Section is now visible, triggering animation');
				runAnimation();
				obs.disconnect();
			});
		},
		{
			threshold: 0.1,
		}
	);

	observer.observe(section);
	console.log('👁️ Observer attached to section');

	// Fallback if already visible
	const rect = section.getBoundingClientRect();

	if (rect.top < window.innerHeight && rect.bottom > 0) {
		console.log('🔄 Section already visible, running animation immediately');
		runAnimation();
	}
}

// Resources page card anim
export function initResourcesCardAnimation() {
	console.log('✅ initResourcesCardAnimation loaded');

	const section = document.querySelector('.bts-resources');

	if (!section) {
		console.error('❌ Section .bts-resources NOT found');
		return;
	}

	console.log('✅ .bts-resources section found');

	if (typeof gsap === 'undefined') {
		console.error('❌ GSAP is NOT loaded');
		return;
	}

	console.log('✅ GSAP is loaded');

	const cards = [...section.querySelectorAll('.bts-resources__card-link')];

	if (cards.length < 1) {
		console.error('❌ No .bts-resources__card-link elements found');
		return;
	}

	console.log(`✅ Found ${cards.length} cards to animate`);

	// Set initial hidden states for all cards
	cards.forEach((card, index) => {
		gsap.set(card, {
			clipPath: 'inset(0 0 100% 0)',
			rotateX: -20,
			scaleY: 1.05,
			transformOrigin: 'top center',
		});
	});

	console.log('✅ Cards hidden states set');

	let animated = false;

	const runAnimation = () => {
		if (animated) return;

		animated = true;
		console.log('🎬 Animation started');

		const tl = gsap.timeline();

		// Animate each card with a slight stagger
		cards.forEach((card, index) => {
			tl.to(
				card,
				{
					clipPath: 'inset(0 0 0% 0)',
					rotateX: 0,
					scaleY: 1,
					duration: 1.5,
					ease: 'power2.inOut',
				},
				index * 0.15 // Stagger each card by 0.15 seconds
			);
		});
	};

	const observer = new IntersectionObserver(
		(entries, obs) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				console.log('👁️ Section is now visible, triggering animation');
				runAnimation();
				obs.disconnect();
			});
		},
		{
			threshold: 0.1,
		}
	);

	observer.observe(section);
	console.log('👁️ Observer attached to section');

	// Fallback if already visible
	const rect = section.getBoundingClientRect();

	if (rect.top < window.innerHeight && rect.bottom > 0) {
		console.log('🔄 Section already visible, running animation immediately');
		runAnimation();
	}
}

export function initVoicesWelcomeAnimation() {
	const PREFIX = 'vw-';
	const sel = (s) => `#${PREFIX}${s}`;

	const stage = document.getElementById('stage');
	const svg = stage && stage.querySelector('svg');
	if (!svg || !window.gsap) {
		if (stage) stage.classList.add('ready');
		return;
	}

	// ── grab groups. Rings are DOM-ordered outer→inner; sort inner-first for the sonar.
	const ringDOM = Array.from(svg.querySelectorAll(`${sel('rings')} .${PREFIX}ring`));
	const rings = ringDOM
		.slice()
		.sort((a, b) => +a.dataset.depth - +b.dataset.depth); // depth 0 = innermost
	const figTop = svg.querySelector(sel('figure-top'));
	const figMid = svg.querySelector(sel('figure-mid'));
	const figBot = svg.querySelector(sel('figure-bot'));

	// Bail out if this SVG doesn't contain the voices-specific elements
	if (!figMid) {
		stage.classList.add('ready');
		return;
	}

	const voicesLetters = Array.from(svg.querySelectorAll(`${sel('voices-text')}  .${PREFIX}letter`));
	const welcomeLetters = Array.from(svg.querySelectorAll(`${sel('welcome-text')} .${PREFIX}letter`));

	// ── initial state (applied BEFORE the SVG becomes visible — no flash)
	gsap.set(rings, { opacity: 0, scale: 0, transformOrigin: '50% 50%' });

	gsap.set([figTop, figMid, figBot], { transformOrigin: '50% 50%' });
	gsap.set(figTop, { opacity: 0, y: -28 });
	gsap.set(figMid, { opacity: 0, scale: 0.9 });
	gsap.set(figBot, { opacity: 0, y: 28 });

	gsap.set(voicesLetters, { opacity: 0, y: -180, scale: 0.4, transformOrigin: '50% 50%' });
	gsap.set(welcomeLetters, { opacity: 0, y: 180, scale: 0.4, transformOrigin: '50% 50%' });

	// safe to reveal now — everything has its starting transform
	stage.classList.add('ready');

	// ── ambient loop (sonar pulse + figure breathing)
	const ambient = [];
	function killAmbient() { while (ambient.length) ambient.pop().kill(); }

	function startAmbient() {
		killAmbient();

		// sonar pulse: rings breathe scale, inner→outer offset → ripple feel
		rings.forEach((el, i) => {
			ambient.push(gsap.to(el, {
				scale: 1.018,
				duration: 1.7 + i * 0.1,
				ease: 'sine.inOut',
				repeat: -1, yoyo: true,
				delay: i * 0.18,
			}));
		});

		// three singers, three breath rhythms
		[figTop, figMid, figBot].forEach((el, i) => {
			ambient.push(gsap.to(el, {
				y: '-=6',
				duration: 2.2 + i * 0.25,
				ease: 'sine.inOut',
				repeat: -1, yoyo: true,
				delay: i * 0.35,
			}));
		});
	}

	// ── intro timeline
	function runIntro() {
		const tl = gsap.timeline({ delay: 0.15, onComplete: startAmbient });

		// 1) sonar reveal — INNER ring blooms first, each outer ring pops out after
		rings.forEach((el, i) => {
			tl.to(el, {
				opacity: 1, scale: 1,
				duration: 0.6, ease: 'back.out(1.6)',
			}, i * 0.16);
		});

		const figStart = 0.55;

		// 2) middle figure (lead singer) materializes
		tl.to(figMid, {
			opacity: 1, scale: 1,
			duration: 0.75, ease: 'back.out(1.5)',
		}, figStart);

		// 3) top + bottom rise into place
		tl.to(figTop, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }, figStart + 0.25);
		tl.to(figBot, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }, figStart + 0.25);

		// 4) text — letters drop / rise with elastic settle, L→R stagger
		const textStart = figStart + 0.55;
		tl.to(voicesLetters, {
			y: 0, opacity: 1, scale: 1,
			duration: 0.9, ease: 'elastic.out(1, 0.55)',
			stagger: 0.07,
		}, textStart);
		tl.to(welcomeLetters, {
			y: 0, opacity: 1, scale: 1,
			duration: 0.9, ease: 'elastic.out(1, 0.55)',
			stagger: 0.07,
		}, textStart);

		return tl;
	}

	function reset() {
		killAmbient();
		if (introTL) introTL.kill();
		gsap.set(rings, { opacity: 0, scale: 0, transformOrigin: '50% 50%' });
		gsap.set(figTop, { opacity: 0, y: -28, x: 0 });
		gsap.set(figMid, { opacity: 0, scale: 0.9, y: 0, x: 0 });
		gsap.set(figBot, { opacity: 0, y: 28, x: 0 });
		gsap.set(voicesLetters, { opacity: 0, y: -180, scale: 0.4 });
		gsap.set(welcomeLetters, { opacity: 0, y: 180, scale: 0.4 });
	}

	let introTL = runIntro();
	const replayBtn = document.getElementById('replay');
	if (replayBtn) {
		replayBtn.addEventListener('click', () => {
			reset();
			introTL = runIntro();
		});
	}
}

export function initNotAloneAnimation() {
	const svg = document.getElementById('not-alone-layer_1');
	if (!svg) {
		return;
	}

	if (svg.style.display === 'none') {
		svg.style.display = 'block';
	}

	// --- 1. Zoom the whole SVG in and show cups immediately ----------------
	setTimeout(() => {
		svg.classList.add('loaded');
		
		const items = svg.querySelectorAll(
			'.tea-glass, [class*="coffe-cup"], [class*="coffee-cup"], .samosa, .snack-plate, .plate'
		);
		items.forEach((item) => {
			try {
				const bbox = item.getBBox();
				item.style.transformOrigin =
					(bbox.x + bbox.width / 2) + 'px ' +
					(bbox.y + bbox.height / 2) + 'px';
			} catch (e) { /* silent */ }
			item.classList.add('pop-in');
		});
	}, 100);

	const zoomDuration = 1500;
	const smokeDelay = 100 + zoomDuration + 500;

	// --- 2. Steam wisps above hot drinks ----------------------------------
	setTimeout(() => {
		const hotItems = svg.querySelectorAll(
			'.tea-glass, [class*="coffe-cup"], [class*="coffee-cup"]'
		);
		hotItems.forEach((item) => {
			try {
				const bbox = item.getBBox();
				const cx = bbox.x + bbox.width / 2;
				const topY = bbox.y - 10;

				const wisps = [
					{
						d: 'M' + (cx - 10) + ',' + topY +
							' C' + (cx - 30) + ',' + (topY - 30) +
							' ' + (cx + 12) + ',' + (topY - 60) +
							' ' + (cx - 10) + ',' + (topY - 90) +
							' C' + (cx - 32) + ',' + (topY - 118) +
							' ' + (cx + 8) + ',' + (topY - 145) +
							' ' + (cx - 6) + ',' + (topY - 170),
						strokeWidth: 9,
						delay: '0s',
						duration: '3.0s',
					},
					{
						d: 'M' + (cx + 16) + ',' + topY +
							' C' + (cx + 36) + ',' + (topY - 28) +
							' ' + (cx - 6) + ',' + (topY - 56) +
							' ' + (cx + 14) + ',' + (topY - 84) +
							' C' + (cx + 34) + ',' + (topY - 112) +
							' ' + (cx + 2) + ',' + (topY - 140) +
							' ' + (cx + 12) + ',' + (topY - 165),
						strokeWidth: 7,
						delay: '1.4s',
						duration: '3.4s',
					},
				];

				wisps.forEach((wisp) => {
					const path = document.createElementNS(
						'http://www.w3.org/2000/svg',
						'path'
					);
					path.setAttribute('d', wisp.d);
					path.setAttribute('stroke-width', wisp.strokeWidth);
					path.classList.add('not-alone-steam-wisp');
					path.style.animationDelay = wisp.delay;
					path.style.animationDuration = wisp.duration;
					svg.appendChild(path);
				});
			} catch (e) { /* silent */ }
		});
	}, 100 + zoomDuration);

	// --- 3. Letters bounce in one-by-one ----------------------------------
	const letters = svg.querySelectorAll('[class*="letter-"], .not-alone-letters');
	letters.forEach((letter, index) => {
		try {
			const bbox = letter.getBBox();
			letter.style.transformOrigin =
				(bbox.x + bbox.width / 2) + 'px ' +
				(bbox.y + bbox.height / 2) + 'px';
		} catch (e) { /* silent */ }

		setTimeout(() => {
			letter.classList.add('not-alone-bounce-in');
		}, smokeDelay + index * 200);
	});
}

// Get Involved banner — allies poster animation
// export function initgetinvlovedanimations() {
// 	const SVGNS = 'http://www.w3.org/2000/svg';
// 	const reduceMotion = window.matchMedia &&
// 		window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// 	function start() {
// 		const svg = document.getElementById('allies-poster') || document.querySelector('svg');
// 		if (!svg) return;
// 		if (reduceMotion) return;

//     /* ---- 1. index every drawable element with its geometry ---- */
//     const nodes = svg.querySelectorAll('path,circle,ellipse,rect,polygon,polyline,line');
//     const items = [];
//     nodes.forEach(function (el) {
//       let b;
//       try { b = el.getBBox(); } catch (e) { return; }
//       if (!b || (b.width === 0 && b.height === 0)) return;
//       items.push({
//         el: el,
//         x: b.x, y: b.y, w: b.width, h: b.height,
//         cx: b.x + b.width / 2,
//         cy: b.y + b.height / 2,
//         cls: el.getAttribute('class') || ''
//       });
//     });

//     function pick(fn) { return items.filter(fn); }
//     function inBox(it, x0, y0, x1, y1) {
//       return it.cx >= x0 && it.cx <= x1 && it.cy >= y0 && it.cy <= y1;
//     }
//     function origin(el, x, y) {
//       el.style.transformBox = 'view-box';
//       el.style.transformOrigin = x + 'px ' + y + 'px';
//     }
//     function tag(el, name) { el.setAttribute('data-anim', name); }

//     /* a small overlay layer (painted last → on top) for particles + ripples */
//     const fxLayer = document.createElementNS(SVGNS, 'g');
//     fxLayer.setAttribute('id', 'allies-fx');
//     svg.appendChild(fxLayer);

//     try { animatePixelHeart(); } catch (e) { console.warn('heart', e); }
//     try { animateWaves(); }      catch (e) { console.warn('waves', e); }
//     try { animateHeaderStars(); } catch (e) { console.warn('headerStars', e); }
//     try { animateArrowHeart(); } catch (e) { console.warn('arrowHeart', e); }
//     try { animateFlags(); }      catch (e) { console.warn('flags', e); }
//     try { animateCursors(); }    catch (e) { console.warn('cursors', e); }
//     try { animateSticks(); }     catch (e) { console.warn('sticks', e); }
//     try { animateSpeakers(); }   catch (e) { console.warn('speakers', e); }
//     try { animateSideDesigns(); } catch (e) { console.warn('sideDesigns', e); }
//     try { animateFlowers(); } catch (e) { console.warn('flowers', e); }
//     try { animateCharacterClouds(300); } catch (e) { console.warn('cloud', e); }
//     try { animateIndianClocks(); } catch (e) { console.warn('clocks', e); }
//     try { animateHeaderChakraSticks(); } catch (e) { console.warn('headerChakras', e); }
//     try { animateCenterBlade(); } catch (e) { console.warn('centerBlade', e); }
//     try { animateDiamondBlink(1800); } catch (e) { console.warn('diamonds', e); }
//     try { animateBannerSticks(); } catch (e) { console.warn('bannerSticks', e); }
//     try { animateKeyboardTyping(); } catch (e) { console.warn('keyboard', e); }

//     /* =====================================================================
//        1) PIXEL HEART — beats like a real heart (double-thump, then a pause)
//        Laptop screen, centred at (834,1237). Heart pixels are small and are
//        everything in the screen box EXCEPT the orange zig-zag waves (cls-34).
//        ===================================================================== */
//     function animatePixelHeart() {
//       const heart = pick(function (it) {
//         return inBox(it, 760, 1165, 905, 1315) &&
//           it.w < 200 && it.h < 220 && it.cls !== 'cls-34';
//       });
//       const pivotX = 834, pivotY = 1237;
//       heart.forEach(function (it) {
//         origin(it.el, pivotX, pivotY);
//         tag(it.el, 'heart-beat');
//         it.el.animate([
//           { transform: 'scale(1)',    offset: 0 },
//           { transform: 'scale(1.07)', offset: 0.10 },
//           { transform: 'scale(1)',    offset: 0.20 },
//           { transform: 'scale(1.13)', offset: 0.32 },
//           { transform: 'scale(1)',    offset: 0.45 },
//           { transform: 'scale(1)',    offset: 1 }
//         ], { duration: 1500, iterations: Infinity, easing: 'ease-in-out' });
//       });
//     }

//     /* =====================================================================
//        2) WAVES — the orange zig-zag chevrons behind the pixel heart drift
//        up and down like water, left & right banks slightly out of phase.
//        ===================================================================== */
//     function animateWaves() {
//       const waves = pick(function (it) {
//         return it.cls === 'cls-34' && inBox(it, 700, 1180, 960, 1300);
//       });
//       waves.forEach(function (it) {
//         const left = it.cx < 834;
//         origin(it.el, it.cx, it.cy);
//         tag(it.el, 'wave');
//         // phase by vertical position so the bank ripples row by row
//         const phase = ((it.cy - 1180) / 120) * 600 + (left ? 0 : 300);
//         it.el.animate([
//           { transform: 'translate(0px,0px) skewX(0deg)' },
//           { transform: 'translate(' + (left ? -4 : 4) + 'px,-6px) skewX(' + (left ? 3 : -3) + 'deg)' },
//           { transform: 'translate(0px,0px) skewX(0deg)' },
//           { transform: 'translate(' + (left ? 4 : -4) + 'px,6px) skewX(' + (left ? -3 : 3) + 'deg)' },
//           { transform: 'translate(0px,0px) skewX(0deg)' }
//         ], { duration: 2600, delay: -phase, iterations: Infinity, easing: 'ease-in-out' });
//       });
//     }

//     /* =====================================================================
//        3) ARROW HEART (bottom) — reveals element-by-element from the inside
//        (left megaphone area and right megaphone area). Fill and outline
//        twinkle together so the border pulses with the star.
//        ===================================================================== */
//     function animateHeaderStars() {
//       [
//         {
//           fill: '.allies-header-star-fill.allies-header-star-right',
//           outlines: '.allies-header-star-outline.allies-header-star-right',
//           delay: 0
//         },
//         {
//           fill: '.allies-header-star-fill.allies-header-star-left',
//           outlines: '.allies-header-star-outline.allies-header-star-left',
//           delay: 500
//         }
//       ].forEach(function (cfg) {
//         const fill = svg.querySelector(cfg.fill);
//         const outlines = svg.querySelectorAll(cfg.outlines);
//         if (!fill) return;

//         let fb;
//         try { fb = fill.getBBox(); } catch (e) { return; }
//         const pivotX = fb.x + fb.width / 2;
//         const pivotY = fb.y + fb.height / 2;

//         function prep(el, name) {
//           origin(el, pivotX, pivotY);
//           tag(el, name);
//         }

//         prep(fill, 'header-star-fill');
//         fill.animate([
//           { opacity: 1, transform: 'scale(1)' },
//           { opacity: 0.5, transform: 'scale(0.7)', offset: 0.42 },
//           { opacity: 1, transform: 'scale(1)', offset: 0.85 },
//           { opacity: 1, transform: 'scale(1)' }
//         ], { duration: 1900, delay: cfg.delay, iterations: Infinity, easing: 'ease-in-out' });

//         outlines.forEach(function (el, idx) {
//           prep(el, 'header-star-outline');
//           el.animate([
//             { opacity: 1, transform: 'scale(1)' },
//             { opacity: 1, transform: 'scale(1.14)', offset: 0.35 },
//             { opacity: 0.8, transform: 'scale(0.92)', offset: 0.62 },
//             { opacity: 1, transform: 'scale(1)' }
//           ], {
//             duration: 1900,
//             delay: cfg.delay + idx * 30,
//             iterations: Infinity,
//             easing: 'ease-in-out'
//           });
//         });
//       });
//     }

//     /* =====================================================================
//        3) ARROW HEART (bottom) — reveals element-by-element from the inside
//        out, and the white double-arrow "exchanges" left<->right.
//        ===================================================================== */
//     function animateArrowHeart() {
//       const heartCx = 834, heartCy = 1950;
//       const parts = pick(function (it) {
//         return inBox(it, 640, 1745, 1030, 2115) && it.w < 250 && it.h < 250;
//       });
//       // reveal inner pieces first → outer frills last
//       parts.sort(function (a, b) {
//         const da = Math.hypot(a.cx - heartCx, a.cy - heartCy);
//         const db = Math.hypot(b.cx - heartCx, b.cy - heartCy);
//         return da - db;
//       });
//       parts.forEach(function (it) {
//         // Elements that are positioned via a presentation transform attribute
//         // must NOT have their CSS transform animated (it would override the
//         // attribute and move them). Those get an opacity-only reveal.
//         it.hasXf = !!it.el.getAttribute('transform');
//         if (!it.hasXf) origin(it.el, it.cx, it.cy);
//         tag(it.el, 'heart-reveal');
//       });

//       function cascade() {
//         const stagger = 15;
//         parts.forEach(function (it, i) {
//           const frames = it.hasXf
//             ? [{ opacity: 1 }, { opacity: 1 }]
//             : [{ opacity: 1, transform: 'scale(1)' },
//                { opacity: 1, transform: 'scale(1.06)', offset: 0.5 },
//                { opacity: 1, transform: 'scale(1)' }];
//           it.el.animate(frames, {
//             duration: 520, delay: i * stagger,
//             easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'forwards'
//           });
//         });
//       }
//       setInterval(cascade, 13000);

//       // the white double-arrow (one #fff path) + its own black outline only:
//       // push outward and back so the two arrow heads "exchange" left<->right.
//       const arrowWhite = pick(function (it) {
//         return it.cls === 'cls-26' && inBox(it, 815, 1955, 855, 2005) && it.w > 300;
//       });
//       const arrowEls = [];
//       arrowWhite.forEach(function (it) {
//         arrowEls.push(it.el);
//         const nxt = it.el.nextElementSibling; // the black outline drawn on top
//         if (nxt && nxt.tagName && nxt.tagName.toLowerCase() === 'path' && !nxt.getAttribute('class')) {
//           arrowEls.push(nxt);
//         }
//       });
//       arrowEls.forEach(function (el) {
//         origin(el, heartCx, 1979);
//         tag(el, 'arrow-exchange');
//         el.animate([
//           { transform: 'scaleX(1) scaleY(1)' },
//           { transform: 'scaleX(1.07) scaleY(0.97)', offset: 0.5 },
//           { transform: 'scaleX(1) scaleY(1)' }
//         ], { duration: 1700, iterations: Infinity, easing: 'ease-in-out' });
//       });
//     }

//     /* =====================================================================
//        4) RESPECT FLAGS — the cloth flutters around its pole like a real
//        flag. Left flag hangs left of its pole (~x548); right flag right of
//        its pole (~x1120). The poles themselves are excluded from the set.
//        ===================================================================== */
//     function animateFlags() {
//       function makeFlag(x0, x1, poleX, dir) {
//         const cloth = pick(function (it) {
//           return it.cx >= x0 && it.cx <= x1 && it.cy >= 1800 && it.cy <= 1928 &&
//             it.w < 200 && it.h < 200;
//         });
//         cloth.forEach(function (it) {
//           origin(it.el, poleX, 1812);
//           tag(it.el, 'flag');
//           it.el.animate([
//             { transform: 'skewX(0deg) skewY(0deg) scaleX(1)' },
//             { transform: 'skewX(' + (5 * dir) + 'deg) skewY(' + (1.5 * dir) + 'deg) scaleX(0.97)', offset: 0.25 },
//             { transform: 'skewX(0deg) skewY(0deg) scaleX(1)', offset: 0.5 },
//             { transform: 'skewX(' + (-5 * dir) + 'deg) skewY(' + (-1.5 * dir) + 'deg) scaleX(0.97)', offset: 0.75 },
//             { transform: 'skewX(0deg) skewY(0deg) scaleX(1)' }
//           ], { duration: 2400, iterations: Infinity, easing: 'ease-in-out' });
//         });
//       }
//       makeFlag(392, 548, 546, 1);    // left flag, waves toward the pole on its right
//       makeFlag(1120, 1276, 1122, -1); // right flag (mirrored)
//     }

//     /* =====================================================================
//        5) MOUSE CURSORS (top, by the handshake) — glide outward, click
//        (with a ripple), and return to the same spot, on a loop.
//        ===================================================================== */
//     function animateCursors() {
//       function withOutline(it) {
//         const set = [it.el];
//         const nxt = it.el.nextElementSibling;
//         if (nxt && nxt.tagName && nxt.tagName.toLowerCase() === 'path' && !nxt.getAttribute('class')) {
//           set.push(nxt);
//         }
//         return set;
//       }
//       function makeCursor(x0, x1, dir) {
//         const found = pick(function (it) {
//           return it.cls === 'cls-26' && it.cx > x0 && it.cx < x1 &&
//             it.cy > 460 && it.cy < 495 && it.w < 60;
//         });
//         if (!found.length) return;
//         const main = found[0];
//         const tipX = dir < 0 ? main.x : main.x + main.w; // pointer tip corner
//         const tipY = main.y;
//         const els = withOutline(main);
//         const dx = 16 * dir, dy = -12;
//         els.forEach(function (el) {
//           origin(el, tipX, tipY);
//           tag(el, 'cursor');
//           el.animate([
//             { transform: 'translate(0,0) scale(1)', offset: 0 },
//             { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.30 },
//             { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.8)', offset: 0.42 },   // press
//             { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.54 },     // release
//             { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.72 },
//             { transform: 'translate(0,0) scale(1)', offset: 1 }
//           ], { duration: 3600, iterations: Infinity, easing: 'ease-in-out' });
//         });
//         // click ripple, fired at the moment of the "press"
//         const rx = tipX + dx, ry = tipY + dy;
//         setInterval(function () { ripple(rx, ry); }, 3600);
//         setTimeout(function () { ripple(rx, ry); }, 0.42 * 3600);
//       }
//       makeCursor(640, 680, -1);   // left cursor
//       makeCursor(986, 1034, 1);   // right cursor
//     }

//     function ripple(x, y) {
//       const c = document.createElementNS(SVGNS, 'circle');
//       c.setAttribute('cx', x); c.setAttribute('cy', y); c.setAttribute('r', 4);
//       c.setAttribute('fill', 'none');
//       c.setAttribute('stroke', '#231f20');
//       c.setAttribute('stroke-width', '2.5');
//       c.setAttribute('class', 'allies-particle');
//       fxLayer.appendChild(c);
//       const a = c.animate([
//         { opacity: 0.9, transform: 'scale(0.2)' },
//         { opacity: 0, transform: 'scale(3.2)' }
//       ], { duration: 650, easing: 'ease-out' });
//       c.style.transformBox = 'view-box';
//       c.style.transformOrigin = x + 'px ' + y + 'px';
//       a.onfinish = function () { c.remove(); };
//     }

//     /* =====================================================================
//        6) THE FIVE STICKS — each one puffs small star symbols out of the
//        circle on top at random intervals; they rise, fall and fade away.
//        ===================================================================== */
//     function animateSticks() {
//       // emit points = centre of each crowning circle (verified via grid)
//       const emitters = [
//         { x: 625, y: 106 }, { x: 730, y: 75 }, { x: 834, y: 45 },
//         { x: 938, y: 75 }, { x: 1043, y: 106 }
//       ];
//       const palette = ['#ffd100', '#ec1e24', '#672e90', '#00a551', '#11a0cc', '#f6921e', '#e6174f', '#15a091'];
//       emitters.forEach(function (e, i) {
//         function puff() {
//           const n = 1 + Math.floor(Math.random() * 2);
//           for (let k = 0; k < n; k++) emitStar(e.x, e.y, palette[(i + k) % palette.length]);
//           setTimeout(puff, 500 + Math.random() * 1100);
//         }
//         setTimeout(puff, i * 250 + Math.random() * 600);
//       });
//     }

//     function starPath(R) {
//       const i = R * 0.34;
//       return 'M0,' + (-R) + 'L' + i + ',' + (-i) + 'L' + R + ',0L' + i + ',' + i +
//         'L0,' + R + 'L' + (-i) + ',' + i + 'L' + (-R) + ',0L' + (-i) + ',' + (-i) + 'Z';
//     }

//     function emitStar(x, y, color) {
//       // a <g> holds the absolute position (static); the inner <path> is
//       // centred at (0,0) and carries the animated transform, so the animation
//       // never fights the positioning.
//       const g = document.createElementNS(SVGNS, 'g');
//       g.setAttribute('transform', 'translate(' + x + ',' + y + ')');
//       g.setAttribute('class', 'allies-particle');
//       const p = document.createElementNS(SVGNS, 'path');
//       const R = 6 + Math.random() * 6;
//       p.setAttribute('d', starPath(R));
//       p.setAttribute('fill', color);
//       p.setAttribute('stroke', '#231f20');
//       p.setAttribute('stroke-width', '0.9');
//       g.appendChild(p);
//       fxLayer.appendChild(g);
//       p.style.transformBox = 'fill-box';
//       p.style.transformOrigin = 'center';
//       const driftX = (Math.random() - 0.5) * 60;
//       const pop = 14 + Math.random() * 16;     // small initial blow-out upward
//       const fall = 70 + Math.random() * 80;    // then falls back down the stick
//       const spin = (Math.random() - 0.5) * 540;
//       const dur = 1500 + Math.random() * 900;
//       const a = p.animate([
//         { transform: 'translate(0px,0px) scale(0.2) rotate(0deg)', opacity: 0 },
//         { transform: 'translate(' + (driftX * 0.4) + 'px,' + (-pop) + 'px) scale(1) rotate(' + (spin * 0.5) + 'deg)', opacity: 1, offset: 0.25 },
//         { transform: 'translate(' + driftX + 'px,' + fall + 'px) scale(0.85) rotate(' + spin + 'deg)', opacity: 0 }
//       ], { duration: dur, easing: 'cubic-bezier(.25,.55,.45,1)' });
//       a.onfinish = function () { g.remove(); };
//     }

//     /* =====================================================================
//        7) TOP MEGAPHONES — star confetti from the yellow horn mouths beside
//        the clocks (.allies-megaphone-mouth-left / -right), not laptop or side mics.
//        ===================================================================== */
//     function animateSpeakers() {
//       const mouths = [
//         { el: svg.querySelector('.allies-megaphone-mouth-left'), dxMin: 30, dxMax: 110, popMin: 18, popMax: 42 },
//         { el: svg.querySelector('.allies-megaphone-mouth-right'), dxMin: -110, dxMax: -30, popMin: 18, popMax: 42 }
//       ];
//       const emitters = mouths.filter(function (m) { return m.el; }).map(function (m) {
//         const b = m.el.getBBox();
//         return {
//           x: b.x + b.width / 2,
//           y: b.y + b.height / 2,
//           dxMin: m.dxMin,
//           dxMax: m.dxMax,
//           popMin: m.popMin,
//           popMax: m.popMax
//         };
//       });
//       if (!emitters.length) return;

//       const palette = ['#15a091', '#ffd100', '#ec1e24', '#00a551', '#11a0cc', '#f6921e', '#672e90', '#ffffff'];
//       emitters.forEach(function (emitter, i) {
//         function sprinkle() {
//           const n = 2 + Math.floor(Math.random() * 3);
//           for (let k = 0; k < n; k++) {
//             emitSprinkle(emitter, palette[Math.floor(Math.random() * palette.length)]);
//           }
//           setTimeout(sprinkle, 320 + Math.random() * 620);
//         }
//         setTimeout(sprinkle, i * 180 + Math.random() * 420);
//       });
//     }

//     function emitSprinkle(emitter, color) {
//       const g = document.createElementNS(SVGNS, 'g');
//       g.setAttribute('transform', 'translate(' + emitter.x + ',' + emitter.y + ')');
//       g.setAttribute('class', 'allies-particle');
//       const p = document.createElementNS(SVGNS, 'path');
//       const R = 11 + Math.random() * 9;
//       p.setAttribute('d', starPath(R));
//       p.setAttribute('fill', color);
//       p.setAttribute('stroke', '#231f20');
//       p.setAttribute('stroke-width', '1.1');
//       g.appendChild(p);
//       fxLayer.appendChild(g);
//       p.style.transformBox = 'fill-box';
//       p.style.transformOrigin = 'center';
//       const driftX = emitter.dxMin + Math.random() * (emitter.dxMax - emitter.dxMin);
//       const pop = emitter.popMin + Math.random() * (emitter.popMax - emitter.popMin);
//       const fall = 55 + Math.random() * 75;
//       const spin = (Math.random() - 0.5) * 720;
//       const dur = 1500 + Math.random() * 900;
//       const a = p.animate([
//         { transform: 'translate(0px,0px) scale(0.35) rotate(0deg)', opacity: 0 },
//         { transform: 'translate(' + (driftX * 0.45) + 'px,' + (-pop) + 'px) scale(1) rotate(' + (spin * 0.35) + 'deg)', opacity: 1, offset: 0.22 },
//         { transform: 'translate(' + driftX + 'px,' + fall + 'px) scale(0.9) rotate(' + spin + 'deg)', opacity: 0 }
//       ], { duration: dur, easing: 'cubic-bezier(.3,.5,.5,1)' });
//       a.onfinish = function () { g.remove(); };
//     }

//     /* =====================================================================
//        8) SIDE DESIGNS — right 1→2→3 and left 1→2→3 in parallel; each pair
//        expands from the center outward (not from the screen edges).
//        ===================================================================== */
//     function animateSideDesigns() {
//       const duration = 420;
//       const pause = 200;
//       const inward = 140;

//       function wait(ms) {
//         return new Promise(function (resolve) { setTimeout(resolve, ms); });
//       }

//       function revealItem(el, fromX) {
//         return new Promise(function (resolve) {
//           if (!el) { resolve(); return; }
//           let b;
//           try { b = el.getBBox(); } catch (e) { resolve(); return; }
//           const ox = b.x + b.width / 2;
//           const oy = b.y + b.height / 2;
//           el.style.transformBox = 'view-box';
//           el.style.transformOrigin = ox + 'px ' + oy + 'px';
//           tag(el, 'side-design');
//           const anim = el.animate([
//             {
//               opacity: 0,
//               visibility: 'hidden',
//               transform: 'translate(' + fromX + 'px, 0px) scale(0.35)'
//             },
//             {
//               opacity: 1,
//               visibility: 'visible',
//               transform: 'translate(0px, 0px) scale(1)',
//               offset: 0.72
//             },
//             {
//               opacity: 1,
//               visibility: 'visible',
//               transform: 'translate(0px, 0px) scale(1)'
//             }
//           ], {
//             duration: duration,
//             easing: 'cubic-bezier(.22,1,.36,1)',
//             fill: 'forwards'
//           });
//           anim.onfinish = resolve;
//         });
//       }

//       const rightItems = [
//         '.allies-right-design-item-1',
//         '.allies-right-design-item-2',
//         '.allies-right-design-item-3'
//       ];
//       const leftItems = [
//         '.allies-left-design-item-1',
//         '.allies-left-design-item-2',
//         '.allies-left-design-item-3'
//       ];

//       (async function run() {
//         for (let i = 0; i < 3; i++) {
//           await Promise.all([
//             revealItem(svg.querySelector(rightItems[i]), -inward),
//             revealItem(svg.querySelector(leftItems[i]), inward)
//           ]);
//           if (i < 2) await wait(pause);
//         }
//       })();
//     }

//     /* =====================================================================
//        8b) FLOWERS — yellow + blue per step slide out together (parallel).
//        Whole flower group moves from centre outward in one smooth motion.
//        ===================================================================== */
//     function animateFlowers() {
//       const duration = 760;
//       const groupGap = 220;
//       const centerX = 834;
//       const centerY = 1267;
//       const slide = 0.52;

//       function wait(ms) {
//         return new Promise(function (resolve) { setTimeout(resolve, ms); });
//       }

//       function revealFlower(el) {
//         if (!el) return;
//         let b;
//         try { b = el.getBBox(); } catch (e) { return; }
//         const ox = b.x + b.width / 2;
//         const oy = b.y + b.height / 2;
//         const fromX = (centerX - ox) * slide;
//         const fromY = (centerY - oy) * slide;
//         el.style.transformBox = 'view-box';
//         el.style.transformOrigin = ox + 'px ' + oy + 'px';
//         tag(el, 'flower');
//         el.animate([
//           {
//             opacity: 0,
//             visibility: 'hidden',
//             transform: 'translate(' + fromX + 'px,' + fromY + 'px) scale(0.9)'
//           },
//           {
//             opacity: 1,
//             visibility: 'visible',
//             transform: 'translate(' + (fromX * 0.05) + 'px,' + (fromY * 0.05) + 'px) scale(1.02)',
//             offset: 0.8
//           },
//           {
//             opacity: 1,
//             visibility: 'visible',
//             transform: 'translate(0px,0px) scale(1)'
//           }
//         ], {
//           duration: duration,
//           easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
//           fill: 'forwards'
//         });
//       }

//       const groups = [
//         ['.allies-l-yellow-flower-1', '.allies-r-yellow-flower-1', '.allies-l-blue-flower-1', '.allies-r-blue-flower-1'],
//         ['.allies-l-yellow-flower-2', '.allies-r-yellow-flower-2', '.allies-l-blue-flower-2', '.allies-r-blue-flower-2'],
//         ['.allies-l-yellow-flower-3', '.allies-r-yellow-flower-3', '.allies-l-blue-flower-3', '.allies-r-blue-flower-3']
//       ];

//       (async function run() {
//         for (let i = 0; i < groups.length; i++) {
//           groups[i].forEach(function (sel) {
//             revealFlower(svg.querySelector(sel));
//           });
//           if (i < groups.length - 1) await wait(groupGap);
//         }
//       })();
//     }

//     /* =====================================================================
//        8c) LIVE IST CLOCKS — custom pointed SVG hands (like banner script),
//        smooth requestAnimationFrame updates with fractional seconds.
//        ===================================================================== */
//     function animateIndianClocks() {
//       const CLOCKS = [
//         { name: 'flower-left', cx: 586.56, cy: 1198.69, r: 50.42 },
//         { name: 'flower-right', cx: 1081.31, cy: 1198.25, r: 50.42 },
//         { name: 'center', cx: 834.72, cy: 45.4, r: 43.56, hourColor: '#f6921e', minuteColor: '#231f20' },
//         { name: 'right-upper', cx: 938.02, cy: 75.14, r: 43.56 },
//         { name: 'right-lower', cx: 1043.11, cy: 106.01, r: 43.56 },
//         { name: 'left-upper', cx: 731.17, cy: 74.67, r: 43.56 },
//         { name: 'left-lower', cx: 626.09, cy: 105.55, r: 43.56 }
//       ];

//       svg.querySelectorAll('.allies-clock-hand').forEach(function (legacy) {
//         legacy.style.opacity = '0';
//         legacy.style.visibility = 'hidden';
//         legacy.style.pointerEvents = 'none';
//       });

//       function roundCoord(value) {
//         return Math.round(value * 100) / 100;
//       }

//       function pointedHandPath(center, length, width) {
//         const cx = center.x;
//         const cy = center.y;
//         const tipY = cy - length;
//         const headLength = Math.min(length * 0.32, Math.max(width * 1.4, 1.55));
//         const neckY = tipY + headLength;
//         const shaftHalf = Math.max(width * 0.2, 0.28);
//         const headHalf = Math.max(width * 0.62, 0.72);
//         const tailY = cy + Math.max(width * 0.28, 0.36);
//         return [
//           'M', roundCoord(cx), roundCoord(tipY),
//           'C', roundCoord(cx + headHalf * 0.9), roundCoord(tipY + headLength * 0.22),
//           roundCoord(cx + headHalf), roundCoord(neckY - headLength * 0.18),
//           roundCoord(cx + shaftHalf), roundCoord(neckY),
//           'L', roundCoord(cx + shaftHalf), roundCoord(tailY),
//           'L', roundCoord(cx - shaftHalf), roundCoord(tailY),
//           'L', roundCoord(cx - shaftHalf), roundCoord(neckY),
//           'C', roundCoord(cx - headHalf), roundCoord(neckY - headLength * 0.18),
//           roundCoord(cx - headHalf * 0.9), roundCoord(tipY + headLength * 0.22),
//           roundCoord(cx), roundCoord(tipY),
//           'Z'
//         ].join(' ');
//       }

//       function lineHand(center, length, width, color) {
//         const line = document.createElementNS(SVGNS, 'line');
//         line.setAttribute('x1', String(center.x));
//         line.setAttribute('y1', String(center.y));
//         line.setAttribute('x2', String(center.x));
//         line.setAttribute('y2', String(center.y - length));
//         line.setAttribute('stroke', color || '#231f20');
//         line.setAttribute('stroke-width', String(width));
//         line.setAttribute('stroke-linecap', 'round');
//         return line;
//       }

//       function setHandRotation(el, angle, cx, cy) {
//         el.setAttribute('transform', 'rotate(' + angle + ' ' + cx + ' ' + cy + ')');
//       }

//       function buildClock(cfg) {
//         const center = { x: cfg.cx, y: cfg.cy };
//         const r = cfg.r;
//         const group = document.createElementNS(SVGNS, 'g');
//         group.setAttribute('class', 'allies-clock-hands allies-clock-' + cfg.name);
//         group.style.pointerEvents = 'none';

//         const hourColor = cfg.hourColor || '#231f20';
//         const minuteColor = cfg.minuteColor || '#231f20';

//         const hour = document.createElementNS(SVGNS, 'path');
//         hour.setAttribute('class', 'allies-clock-hour');
//         hour.setAttribute('d', pointedHandPath(center, r * 0.43, Math.max(r * 0.085, 1.1)));
//         hour.setAttribute('fill', hourColor);
//         hour.setAttribute('stroke', hourColor);
//         hour.setAttribute('stroke-width', String(Math.max(r * 0.014, 0.18)));
//         hour.setAttribute('stroke-linejoin', 'round');
//         tag(hour, 'clock');

//         const minute = document.createElementNS(SVGNS, 'path');
//         minute.setAttribute('class', 'allies-clock-minute');
//         minute.setAttribute('d', pointedHandPath(center, r * 0.61, Math.max(r * 0.065, 0.85)));
//         minute.setAttribute('fill', minuteColor);
//         minute.setAttribute('stroke', minuteColor);
//         minute.setAttribute('stroke-width', String(Math.max(r * 0.011, 0.16)));
//         minute.setAttribute('stroke-linejoin', 'round');
//         tag(minute, 'clock');

//         group.appendChild(hour);
//         group.appendChild(minute);

//         let second = null;
//         if (!cfg.staticTime) {
//           second = lineHand(center, r * 0.74, 0.55, '#e6174f');
//           second.setAttribute('class', 'allies-clock-second');
//           tag(second, 'clock');
//           group.appendChild(second);
//         }

//         const pin = document.createElementNS(SVGNS, 'circle');
//         pin.setAttribute('class', 'allies-clock-pin');
//         pin.setAttribute('cx', String(cfg.cx));
//         pin.setAttribute('cy', String(cfg.cy));
//         pin.setAttribute('r', String(Math.max(0.8, r * 0.09)));
//         pin.setAttribute('fill', '#231f20');

//         group.appendChild(pin);
//         fxLayer.appendChild(group);

//         return { hour: hour, minute: minute, second: second, cx: cfg.cx, cy: cfg.cy, cfg: cfg };
//       }

//       const allHands = CLOCKS.map(buildClock);
//       const liveHands = [];
//       const spinHands = [];
//       CLOCKS.forEach(function (cfg, i) {
//         if (cfg.staticTime) return;
//         if (cfg.spin) spinHands.push(allHands[i]);
//         else liveHands.push(allHands[i]);
//       });

//       function syncStaticClocks() {
//         allHands.forEach(function (hands) {
//           const t = hands.cfg.staticTime;
//           if (!t) return;
//           const hourAngle = (t.hour % 12) * 30 + t.minute * 0.5;
//           const minuteAngle = t.minute * 6;
//           setHandRotation(hands.minute, minuteAngle, hands.cx, hands.cy);
//           setHandRotation(hands.hour, hourAngle, hands.cx, hands.cy);
//         });
//       }

//       const spinStart = performance.now();

//       function getISTInstant() {
//         const now = new Date();
//         const parts = { hour: 0, minute: 0, second: 0 };
//         new Intl.DateTimeFormat('en-GB', {
//           timeZone: 'Asia/Kolkata',
//           hour: '2-digit',
//           minute: '2-digit',
//           second: '2-digit',
//           hour12: false
//         }).formatToParts(now).forEach(function (p) {
//           if (p.type in parts) parts[p.type] = Number(p.value);
//         });
//         const hour24 = parts.hour === 24 ? 0 : parts.hour;
//         const ms = now.getMilliseconds();
//         const seconds = parts.second + ms / 1000;
//         const minutes = parts.minute + seconds / 60;
//         const hours = (hour24 % 12) + minutes / 60;
//         return { hours: hours, minutes: minutes, seconds: seconds };
//       }

//       function syncClocks() {
//         const t = getISTInstant();
//         liveHands.forEach(function (hands) {
//           setHandRotation(hands.second, t.seconds * 6, hands.cx, hands.cy);
//           setHandRotation(hands.minute, t.minutes * 6, hands.cx, hands.cy);
//           setHandRotation(hands.hour, t.hours * 30, hands.cx, hands.cy);
//         });
//       }

//       function syncSpinClocks() {
//         const elapsed = (performance.now() - spinStart) / 1000;
//         spinHands.forEach(function (hands) {
//           setHandRotation(hands.second, (elapsed * 96) % 360, hands.cx, hands.cy);
//           setHandRotation(hands.minute, (elapsed * 36) % 360, hands.cx, hands.cy);
//           setHandRotation(hands.hour, (elapsed * 12) % 360, hands.cx, hands.cy);
//         });
//       }

//       function tick() {
//         syncClocks();
//         syncSpinClocks();
//         window.__alliesClockFrame = requestAnimationFrame(tick);
//       }

//       if (window.__alliesClockFrame) {
//         cancelAnimationFrame(window.__alliesClockFrame);
//       }
//       syncStaticClocks();
//       syncClocks();
//       syncSpinClocks();
//       tick();
//     }

//     /* =====================================================================
//        8d) HEADER CHAKRA STICKS — rotate inner star + hand paths only;
//        the outer chakra ring (cls-31) and tick marks stay fixed.
//        ===================================================================== */
//     function animateHeaderChakraSticks() {
//       const CHAKRAS = [
//         { name: 'header-left', cx: 227.68, cy: 281.9, r: 52 },
//         { name: 'header-right', cx: 1439.77, cy: 280.91, r: 52 }
//       ];

//       function isChakraStick(it, cfg) {
//         if (!inBox(it, cfg.cx - cfg.r, cfg.cy - cfg.r, cfg.cx + cfg.r, cfg.cy + cfg.r)) {
//           return false;
//         }
//         if (it.cls === 'cls-88' || it.cls === 'cls-3') return true;
//         if (it.el.tagName === 'path' && !it.cls) {
//           const d = it.el.getAttribute('d') || '';
//           if (d.indexOf('M1454.03') === 0 || d.indexOf('M213.83') === 0) return true;
//         }
//         return false;
//       }

//       function setStickRotation(el, angle, cx, cy, baseTransform) {
//         const rot = 'rotate(' + angle + ' ' + cx + ' ' + cy + ')';
//         el.setAttribute('transform', baseTransform ? baseTransform + ' ' + rot : rot);
//       }

//       const groups = CHAKRAS.map(function (cfg) {
//         const sticks = pick(function (it) { return isChakraStick(it, cfg); });
//         sticks.forEach(function (it) { tag(it.el, 'chakra-stick'); });
//         return {
//           cx: cfg.cx,
//           cy: cfg.cy,
//           sticks: sticks,
//           baseTransforms: sticks.map(function (it) {
//             return it.el.getAttribute('transform') || '';
//           })
//         };
//       });

//       const spinStart = performance.now();

//       function tick() {
//         const elapsed = (performance.now() - spinStart) / 1000;
//         const angle = (elapsed * 18) % 360;
//         groups.forEach(function (grp) {
//           grp.sticks.forEach(function (it, i) {
//             setStickRotation(it.el, angle, grp.cx, grp.cy, grp.baseTransforms[i]);
//           });
//         });
//         window.__alliesHeaderChakraFrame = requestAnimationFrame(tick);
//       }

//       if (window.__alliesHeaderChakraFrame) {
//         cancelAnimationFrame(window.__alliesHeaderChakraFrame);
//       }
//       tick();
//     }

//     /* =====================================================================
//        8e) CENTER MANDALA BLADE — yellow star at ~844,1630; two paths only.
//        ===================================================================== */
//     function animateCenterBlade() {
//       const BLADE_D = ['M844.89,1630.16', 'M843.27,1631.11'];
//       const pivotX = 844.08;
//       const pivotY = 1630.63;

//       const blades = pick(function (it) {
//         if (it.el.tagName !== 'path') return false;
//         const d = it.el.getAttribute('d') || '';
//         return BLADE_D.some(function (sig) { return d.indexOf(sig) === 0; });
//       });

//       if (!blades.length) return;

//       const baseTransforms = blades.map(function (it) {
//         tag(it.el, 'center-blade');
//         return it.el.getAttribute('transform') || '';
//       });

//       const spinStart = performance.now();

//       function tick() {
//         const angle = ((performance.now() - spinStart) / 1000) * 8 % 360;
//         blades.forEach(function (it, i) {
//           const rot = 'rotate(' + angle + ' ' + pivotX + ' ' + pivotY + ')';
//           const base = baseTransforms[i];
//           it.el.setAttribute('transform', base ? base + ' ' + rot : rot);
//         });
//         window.__alliesBladeFrame = requestAnimationFrame(tick);
//       }

//       if (window.__alliesBladeFrame) {
//         cancelAnimationFrame(window.__alliesBladeFrame);
//       }
//       tick();
//     }

//     /* =====================================================================
//        9) CHARACTER CLOUDS — scale outward, then a fixed set of rain strokes.
//        ===================================================================== */
//     function animateCharacterClouds(delay) {
//       const rainLayer = document.createElementNS(SVGNS, 'g');
//       rainLayer.setAttribute('id', 'allies-rain-layer');
//       rainLayer.setAttribute('class', 'allies-rain');
//       fxLayer.appendChild(rainLayer);

//       const RAIN_DROPS = 6;
//       const cloudConfigs = [];

//       [
//         { sel: '.allies-right-cloud' },
//         { sel: '.allies-left-cloud' }
//       ].forEach(function (cfg, index) {
//         const cloud = svg.querySelector(cfg.sel);
//         if (!cloud) return;

//         let b;
//         try { b = cloud.getBBox(); } catch (e) { return; }
//         const pivotX = b.x + b.width / 2;
//         const pivotY = b.y + b.height / 2;
//         cloud.style.transformBox = 'view-box';
//         cloud.style.transformOrigin = pivotX + 'px ' + pivotY + 'px';
//         tag(cloud, 'cloud');

//         const cloudDelay = delay + index * 100;
//         cloud.animate([
//           { transform: 'scale(1)' },
//           { transform: 'scale(1.04)', offset: 0.5 },
//           { transform: 'scale(1)' }
//         ], {
//           duration: 3200,
//           delay: cloudDelay,
//           iterations: Infinity,
//           easing: 'ease-in-out'
//         });

//         cloudConfigs.push({
//           pivotX: pivotX,
//           spread: Math.max(28, b.width * 0.24),
//           baseY: b.y + b.height * 0.95,
//           rainStart: cloudDelay + 320
//         });
//       });

//       if (!cloudConfigs.length) return;

//       setTimeout(function () {
//         for (let i = 0; i < RAIN_DROPS; i++) {
//           const cfg = cloudConfigs[i % cloudConfigs.length];
//           const slot = Math.floor(i / cloudConfigs.length);
//           const x = cfg.pivotX + (Math.random() - 0.5) * cfg.spread * 1.4;
//           const y = cfg.baseY + slot * 5 + Math.random() * 4;
//           emitRainDrop(rainLayer, x, y, 50 + Math.random() * 45);
//         }
//       }, Math.max.apply(null, cloudConfigs.map(function (c) { return c.rainStart; })));
//     }

//     function emitRainDrop(layer, x, y, fallDistance) {
//       const len = 12 + Math.random() * 12;
//       const drift = (Math.random() - 0.5) * 10;
//       const opacity = 0.55 + Math.random() * 0.35;
//       const line = document.createElementNS(SVGNS, 'line');
//       line.setAttribute('x1', String(x));
//       line.setAttribute('y1', String(y));
//       line.setAttribute('x2', String(x + drift * 0.2));
//       line.setAttribute('y2', String(y + len));
//       line.setAttribute('stroke', '#2b7fd4');
//       line.setAttribute('stroke-width', '3.2');
//       line.setAttribute('stroke-linecap', 'round');
//       line.setAttribute('opacity', String(opacity));
//       layer.appendChild(line);

//       const fall = fallDistance || (60 + Math.random() * 40);
//       const dur = 520 + Math.random() * 360;
//       const x1 = x;
//       let y1 = y;
//       const x2 = x + drift;
//       const y2 = y + fall;
//       let start = 0;

//       function frame(ts) {
//         if (!start) start = ts;
//         const t = Math.min(1, (ts - start) / dur);
//         const ease = 1 - Math.pow(1 - t, 2);
//         const cy = y1 + (y2 - y1) * ease;
//         const cx = x1 + (x2 - x1) * ease;
//         line.setAttribute('x1', String(cx));
//         line.setAttribute('y1', String(cy - len));
//         line.setAttribute('x2', String(cx + drift * 0.15));
//         line.setAttribute('y2', String(cy));
//         if (t < 1) {
//           requestAnimationFrame(frame);
//         } else {
//           start = 0;
//           y1 = y - Math.random() * 8;
//           requestAnimationFrame(frame);
//         }
//       }
//       requestAnimationFrame(frame);
//     }

//     /* =====================================================================
//        10) DIAMOND LEDs — left & right columns blink together in sync.
//        ===================================================================== */
//     function animateDiamondBlink(delay) {
//       const RED = '#ec1e24';
//       const DIM = 0.38;
//       const BRIGHT = 1;
//       const BLINK_MS = 720;

//       const leftLeds = svg.querySelectorAll('.allies-led-column-left .allies-led');
//       const rightLeds = svg.querySelectorAll('.allies-led-column-right .allies-led');
//       const all = [];
//       leftLeds.forEach(function (el) { all.push(el); });
//       rightLeds.forEach(function (el) { all.push(el); });
//       if (!all.length) return;

//       all.forEach(function (el) {
//         el.setAttribute('fill', RED);
//         el.style.opacity = String(DIM);
//         tag(el, 'diamond-led');
//       });

//       function blink() {
//         all.forEach(function (el) {
//           el.animate([
//             { opacity: DIM },
//             { opacity: BRIGHT, offset: 0.42 },
//             { opacity: DIM }
//           ], { duration: BLINK_MS, easing: 'ease-in-out' });
//         });
//       }

//       setTimeout(function () {
//         blink();
//         if (window.__alliesDiamondTimer) clearInterval(window.__alliesDiamondTimer);
//         window.__alliesDiamondTimer = setInterval(blink, BLINK_MS);
//       }, delay);
//     }

//     /* =====================================================================
//        11) BANNER STICKS — purple / yellow / teal fan bars left & right of
//            the ALLIES MATTER strip (cls-88, cls-38, cls-90 rects ~y 1774).
//        ===================================================================== */
//     function animateBannerSticks() {
//       const sticks = pick(function (it) {
//         return it.el.tagName === 'rect' &&
//           it.y >= 1770 && it.y <= 1795 &&
//           it.h >= 105 && it.h <= 115 &&
//           (it.cls === 'cls-88' || it.cls === 'cls-38' || it.cls === 'cls-90');
//       });

//       if (!sticks.length) return;

//       sticks.sort(function (a, b) { return a.cx - b.cx; });

//       sticks.forEach(function (it, i) {
//         const pivotY = it.y + it.h;
//         origin(it.el, it.cx, pivotY);
//         tag(it.el, 'banner-stick');

//         const side = it.cx < 834 ? -1 : 1;
//         const group = sticks.filter(function (s) {
//           return side < 0 ? s.cx < 834 : s.cx > 834;
//         });
//         const order = group.indexOf(it);
//         const baseAngle = side * (order - 1) * 6;

//         function sway() {
//           it.el.animate([
//             { transform: 'rotate(' + baseAngle + 'deg) scale(1)' },
//             {
//               transform: 'rotate(' + (baseAngle + side * 14) + 'deg) scale(1.04)',
//               offset: 0.5
//             },
//             { transform: 'rotate(' + baseAngle + 'deg) scale(1)' }
//           ], { duration: 700 + order * 130, easing: 'ease-in-out' });
//         }

//         setTimeout(function () {
//           sway();
//           setInterval(sway, 1100 + order * 180);
//         }, 500 + i * 90);
//       });
//     }

//     /* =====================================================================
//        12) KEYBOARDS — laptop keys + side piano strips type with key presses.
//        ===================================================================== */
//     function animateKeyboardTyping() {
//       const keys = [];
//       const KEY_COLORS = [
//         '#ffd100', '#ec1e24', '#672e90', '#11a0cc',
//         '#00a551', '#f6921e', '#ef8200', '#be4c97'
//       ];

//       pick(function (it) {
//         return it.el.tagName === 'rect' && it.cls === 'cls-28' &&
//           it.cy >= 1775 && it.cy <= 1885 && (it.cx < 520 || it.cx > 1140);
//       }).forEach(function (it) { keys.push(it); });

//       pick(function (it) {
//         return inBox(it, 688, 1343, 972, 1398) &&
//           it.h <= 14 && it.w >= 12 && it.w <= 180 &&
//           it.cls !== 'cls-35' && it.cls !== 'cls-21' && it.cls !== 'cls-3' &&
//           it.cls !== 'cls-97';
//       }).forEach(function (it) { keys.push(it); });

//       if (!keys.length) return;

//       keys.sort(function (a, b) {
//         return a.cy - b.cy || a.cx - b.cx;
//       });

//       let index = 0;
//       let colorIndex = 0;

//       function pressKey() {
//         const it = keys[index % keys.length];
//         index += 1;
//         const el = it.el;
//         const origFill = el.getAttribute('fill');
//         const color = KEY_COLORS[colorIndex % KEY_COLORS.length];
//         colorIndex += 1;

//         origin(el, it.cx, it.cy);
//         tag(el, 'keyboard-key');
//         el.setAttribute('fill', color);

//         const anim = el.animate([
//           { opacity: 1, transform: 'scale(1)' },
//           { opacity: 1, transform: 'scale(0.88)', offset: 0.35 },
//           { opacity: 1, transform: 'scale(1)' }
//         ], { duration: 110, easing: 'ease-out' });

//         anim.onfinish = function () {
//           if (origFill) el.setAttribute('fill', origFill);
//           else el.removeAttribute('fill');
//         };
//       }

//       setInterval(pressKey, 95);
//     }
//   }
// 	function boot() {
// 		requestAnimationFrame(function () {
// 			requestAnimationFrame(start);
// 		});
// 	}

// 	boot();
// }

export function initgetinvlovedanimations() {
	const svg = document.getElementById('allies-poster');
  	if (!svg) return; 
	const SVGNS = 'http://www.w3.org/2000/svg';
	const reduceMotion = window.matchMedia &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	function start() {
		const svg = document.getElementById('allies-poster');
  		if (!svg || reduceMotion) return;
		if (reduceMotion) return;

    /* ---- 1. index every drawable element with its geometry ---- */
    const nodes = svg.querySelectorAll('path,circle,ellipse,rect,polygon,polyline,line');
    const items = [];
    nodes.forEach(function (el) {
      let b;
      try { b = el.getBBox(); } catch (e) { return; }
      if (!b || (b.width === 0 && b.height === 0)) return;
      items.push({
        el: el,
        x: b.x, y: b.y, w: b.width, h: b.height,
        cx: b.x + b.width / 2,
        cy: b.y + b.height / 2,
        cls: el.getAttribute('class') || ''
      });
    });

    function pick(fn) { return items.filter(fn); }
    function inBox(it, x0, y0, x1, y1) {
      return it.cx >= x0 && it.cx <= x1 && it.cy >= y0 && it.cy <= y1;
    }
    function origin(el, x, y) {
      el.style.transformBox = 'view-box';
      el.style.transformOrigin = x + 'px ' + y + 'px';
    }
    function tag(el, name) { el.setAttribute('data-anim', name); }

    /* a small overlay layer (painted last → on top) for particles + ripples */
    const fxLayer = document.createElementNS(SVGNS, 'g');
    fxLayer.setAttribute('id', 'allies-fx');
    svg.appendChild(fxLayer);

    try { animatePixelHeart(); } catch (e) { console.warn('heart', e); }
    try { animateWaves(); }      catch (e) { console.warn('waves', e); }
    try { animateHeaderStars(); } catch (e) { console.warn('headerStars', e); }
    try { animateArrowHeart(); } catch (e) { console.warn('arrowHeart', e); }
    try { animateFlags(); }      catch (e) { console.warn('flags', e); }
    try { animateCursors(); }    catch (e) { console.warn('cursors', e); }
    try { animateSticks(); }     catch (e) { console.warn('sticks', e); }
    try { animateSpeakers(); }   catch (e) { console.warn('speakers', e); }
    try { animateSideDesigns(); } catch (e) { console.warn('sideDesigns', e); }
    try { animateFlowers(); } catch (e) { console.warn('flowers', e); }
    try { animateCharacterClouds(300); } catch (e) { console.warn('cloud', e); }
    try { animateIndianClocks(); } catch (e) { console.warn('clocks', e); }
    try { animateHeaderChakraSticks(); } catch (e) { console.warn('headerChakras', e); }
    try { animateCenterBlade(); } catch (e) { console.warn('centerBlade', e); }
    try { animateDiamondBlink(1800); } catch (e) { console.warn('diamonds', e); }
    try { animateBannerSticks(); } catch (e) { console.warn('bannerSticks', e); }
    try { animateKeyboardTyping(); } catch (e) { console.warn('keyboard', e); }

    /* =====================================================================
       1) PIXEL HEART — beats like a real heart (double-thump, then a pause)
       Laptop screen, centred at (834,1237). Heart pixels are small and are
       everything in the screen box EXCEPT the orange zig-zag waves (cls-34).
       ===================================================================== */
    function animatePixelHeart() {
      const heart = pick(function (it) {
        return inBox(it, 760, 1165, 905, 1315) &&
          it.w < 200 && it.h < 220 && it.cls !== 'cls-34';
      });
      const pivotX = 834, pivotY = 1237;
      heart.forEach(function (it) {
        origin(it.el, pivotX, pivotY);
        tag(it.el, 'heart-beat');
        it.el.animate([
          { transform: 'scale(1)',    offset: 0 },
          { transform: 'scale(1.07)', offset: 0.10 },
          { transform: 'scale(1)',    offset: 0.20 },
          { transform: 'scale(1.13)', offset: 0.32 },
          { transform: 'scale(1)',    offset: 0.45 },
          { transform: 'scale(1)',    offset: 1 }
        ], { duration: 1500, iterations: Infinity, easing: 'ease-in-out' });
      });
    }

    /* =====================================================================
       2) WAVES — the orange zig-zag chevrons behind the pixel heart drift
       up and down like water, left & right banks slightly out of phase.
       ===================================================================== */
    function animateWaves() {
      const waves = pick(function (it) {
        return it.cls === 'cls-34' && inBox(it, 700, 1180, 960, 1300);
      });
      waves.forEach(function (it) {
        const left = it.cx < 834;
        origin(it.el, it.cx, it.cy);
        tag(it.el, 'wave');
        // phase by vertical position so the bank ripples row by row
        const phase = ((it.cy - 1180) / 120) * 600 + (left ? 0 : 300);
        it.el.animate([
          { transform: 'translate(0px,0px) skewX(0deg)' },
          { transform: 'translate(' + (left ? -4 : 4) + 'px,-6px) skewX(' + (left ? 3 : -3) + 'deg)' },
          { transform: 'translate(0px,0px) skewX(0deg)' },
          { transform: 'translate(' + (left ? 4 : -4) + 'px,6px) skewX(' + (left ? -3 : 3) + 'deg)' },
          { transform: 'translate(0px,0px) skewX(0deg)' }
        ], { duration: 2600, delay: -phase, iterations: Infinity, easing: 'ease-in-out' });
      });
    }

    /* =====================================================================
       3) ARROW HEART (bottom) — reveals element-by-element from the inside
       (left megaphone area and right megaphone area). Fill and outline
       twinkle together so the border pulses with the star.
       ===================================================================== */
    function animateHeaderStars() {
      [
        {
          fill: '.allies-header-star-fill.allies-header-star-right',
          outlines: '.allies-header-star-outline.allies-header-star-right',
          delay: 0
        },
        {
          fill: '.allies-header-star-fill.allies-header-star-left',
          outlines: '.allies-header-star-outline.allies-header-star-left',
          delay: 500
        }
      ].forEach(function (cfg) {
        const fill = svg.querySelector(cfg.fill);
        const outlines = svg.querySelectorAll(cfg.outlines);
        if (!fill) return;

        let fb;
        try { fb = fill.getBBox(); } catch (e) { return; }
        const pivotX = fb.x + fb.width / 2;
        const pivotY = fb.y + fb.height / 2;

        function prep(el, name) {
          origin(el, pivotX, pivotY);
          tag(el, name);
        }

        prep(fill, 'header-star-fill');
        fill.animate([
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0.5, transform: 'scale(0.7)', offset: 0.42 },
          { opacity: 1, transform: 'scale(1)', offset: 0.85 },
          { opacity: 1, transform: 'scale(1)' }
        ], { duration: 1900, delay: cfg.delay, iterations: Infinity, easing: 'ease-in-out' });

        outlines.forEach(function (el, idx) {
          prep(el, 'header-star-outline');
          el.animate([
            { opacity: 1, transform: 'scale(1)' },
            { opacity: 1, transform: 'scale(1.14)', offset: 0.35 },
            { opacity: 0.8, transform: 'scale(0.92)', offset: 0.62 },
            { opacity: 1, transform: 'scale(1)' }
          ], {
            duration: 1900,
            delay: cfg.delay + idx * 30,
            iterations: Infinity,
            easing: 'ease-in-out'
          });
        });
      });
    }

    /* =====================================================================
       3) ARROW HEART (bottom) — reveals element-by-element from the inside
       out, and the white double-arrow "exchanges" left<->right.
       ===================================================================== */
    function animateArrowHeart() {
      const heartCx = 834, heartCy = 1950;
      const parts = pick(function (it) {
        return inBox(it, 640, 1745, 1030, 2115) && it.w < 250 && it.h < 250;
      });
      // reveal inner pieces first → outer frills last
      parts.sort(function (a, b) {
        const da = Math.hypot(a.cx - heartCx, a.cy - heartCy);
        const db = Math.hypot(b.cx - heartCx, b.cy - heartCy);
        return da - db;
      });
      parts.forEach(function (it) {
        // Elements that are positioned via a presentation transform attribute
        // must NOT have their CSS transform animated (it would override the
        // attribute and move them). Those get an opacity-only reveal.
        it.hasXf = !!it.el.getAttribute('transform');
        if (!it.hasXf) origin(it.el, it.cx, it.cy);
        tag(it.el, 'heart-reveal');
      });

      function cascade() {
        const stagger = 15;
        parts.forEach(function (it, i) {
          const frames = it.hasXf
            ? [{ opacity: 1 }, { opacity: 1 }]
            : [{ opacity: 1, transform: 'scale(1)' },
               { opacity: 1, transform: 'scale(1.06)', offset: 0.5 },
               { opacity: 1, transform: 'scale(1)' }];
          it.el.animate(frames, {
            duration: 520, delay: i * stagger,
            easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'forwards'
          });
        });
      }
      setInterval(cascade, 13000);

      // the white double-arrow (one #fff path) + its own black outline only:
      // push outward and back so the two arrow heads "exchange" left<->right.
      const arrowWhite = pick(function (it) {
        return it.cls === 'cls-26' && inBox(it, 815, 1955, 855, 2005) && it.w > 300;
      });
      const arrowEls = [];
      arrowWhite.forEach(function (it) {
        arrowEls.push(it.el);
        const nxt = it.el.nextElementSibling; // the black outline drawn on top
        if (nxt && nxt.tagName && nxt.tagName.toLowerCase() === 'path' && !nxt.getAttribute('class')) {
          arrowEls.push(nxt);
        }
      });
      arrowEls.forEach(function (el) {
        origin(el, heartCx, 1979);
        tag(el, 'arrow-exchange');
        el.animate([
          { transform: 'scaleX(1) scaleY(1)' },
          { transform: 'scaleX(1.07) scaleY(0.97)', offset: 0.5 },
          { transform: 'scaleX(1) scaleY(1)' }
        ], { duration: 1700, iterations: Infinity, easing: 'ease-in-out' });
      });
    }

    /* =====================================================================
       4) RESPECT FLAGS — the cloth flutters around its pole like a real
       flag. Left flag hangs left of its pole (~x548); right flag right of
       its pole (~x1120). The poles themselves are excluded from the set.
       ===================================================================== */
    function animateFlags() {
      function makeFlag(x0, x1, poleX, dir) {
        const cloth = pick(function (it) {
          return it.cx >= x0 && it.cx <= x1 && it.cy >= 1800 && it.cy <= 1928 &&
            it.w < 200 && it.h < 200;
        });
        cloth.forEach(function (it) {
          origin(it.el, poleX, 1812);
          tag(it.el, 'flag');
          it.el.animate([
            { transform: 'skewX(0deg) skewY(0deg) scaleX(1)' },
            { transform: 'skewX(' + (5 * dir) + 'deg) skewY(' + (1.5 * dir) + 'deg) scaleX(0.97)', offset: 0.25 },
            { transform: 'skewX(0deg) skewY(0deg) scaleX(1)', offset: 0.5 },
            { transform: 'skewX(' + (-5 * dir) + 'deg) skewY(' + (-1.5 * dir) + 'deg) scaleX(0.97)', offset: 0.75 },
            { transform: 'skewX(0deg) skewY(0deg) scaleX(1)' }
          ], { duration: 2400, iterations: Infinity, easing: 'ease-in-out' });
        });
      }
      makeFlag(392, 548, 546, 1);    // left flag, waves toward the pole on its right
      makeFlag(1120, 1276, 1122, -1); // right flag (mirrored)
    }

    /* =====================================================================
       5) MOUSE CURSORS (top, by the handshake) — glide outward, click
       (with a ripple), and return to the same spot, on a loop.
       ===================================================================== */
    function animateCursors() {
      function withOutline(it) {
        const set = [it.el];
        const nxt = it.el.nextElementSibling;
        if (nxt && nxt.tagName && nxt.tagName.toLowerCase() === 'path' && !nxt.getAttribute('class')) {
          set.push(nxt);
        }
        return set;
      }
      function makeCursor(x0, x1, dir) {
        const found = pick(function (it) {
          return it.cls === 'cls-26' && it.cx > x0 && it.cx < x1 &&
            it.cy > 460 && it.cy < 495 && it.w < 60;
        });
        if (!found.length) return;
        const main = found[0];
        const tipX = dir < 0 ? main.x : main.x + main.w; // pointer tip corner
        const tipY = main.y;
        const els = withOutline(main);
        const dx = 16 * dir, dy = -12;
        els.forEach(function (el) {
          origin(el, tipX, tipY);
          tag(el, 'cursor');
          el.animate([
            { transform: 'translate(0,0) scale(1)', offset: 0 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.30 },
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.8)', offset: 0.42 },   // press
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.54 },     // release
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(1)', offset: 0.72 },
            { transform: 'translate(0,0) scale(1)', offset: 1 }
          ], { duration: 3600, iterations: Infinity, easing: 'ease-in-out' });
        });
        // click ripple, fired at the moment of the "press"
        const rx = tipX + dx, ry = tipY + dy;
        setInterval(function () { ripple(rx, ry); }, 3600);
        setTimeout(function () { ripple(rx, ry); }, 0.42 * 3600);
      }
      makeCursor(640, 680, -1);   // left cursor
      makeCursor(986, 1034, 1);   // right cursor
    }

    function ripple(x, y) {
      const c = document.createElementNS(SVGNS, 'circle');
      c.setAttribute('cx', x); c.setAttribute('cy', y); c.setAttribute('r', 4);
      c.setAttribute('fill', 'none');
      c.setAttribute('stroke', '#231f20');
      c.setAttribute('stroke-width', '2.5');
      c.setAttribute('class', 'allies-particle');
      fxLayer.appendChild(c);
      const a = c.animate([
        { opacity: 0.9, transform: 'scale(0.2)' },
        { opacity: 0, transform: 'scale(3.2)' }
      ], { duration: 650, easing: 'ease-out' });
      c.style.transformBox = 'view-box';
      c.style.transformOrigin = x + 'px ' + y + 'px';
      a.onfinish = function () { c.remove(); };
    }

    /* =====================================================================
       6) THE FIVE STICKS — each one puffs small star symbols out of the
       circle on top at random intervals; they rise, fall and fade away.
       ===================================================================== */
    function animateSticks() {
      // emit points = centre of each crowning circle (verified via grid)
      const emitters = [
        { x: 625, y: 106 }, { x: 730, y: 75 }, { x: 834, y: 45 },
        { x: 938, y: 75 }, { x: 1043, y: 106 }
      ];
      const palette = ['#ffd100', '#ec1e24', '#672e90', '#00a551', '#11a0cc', '#f6921e', '#e6174f', '#15a091'];
      emitters.forEach(function (e, i) {
        function puff() {
          const n = 1 + Math.floor(Math.random() * 2);
          for (let k = 0; k < n; k++) emitStar(e.x, e.y, palette[(i + k) % palette.length]);
          setTimeout(puff, 500 + Math.random() * 1100);
        }
        setTimeout(puff, i * 250 + Math.random() * 600);
      });
    }

    function starPath(R) {
      const i = R * 0.34;
      return 'M0,' + (-R) + 'L' + i + ',' + (-i) + 'L' + R + ',0L' + i + ',' + i +
        'L0,' + R + 'L' + (-i) + ',' + i + 'L' + (-R) + ',0L' + (-i) + ',' + (-i) + 'Z';
    }

    function emitStar(x, y, color) {
      // a <g> holds the absolute position (static); the inner <path> is
      // centred at (0,0) and carries the animated transform, so the animation
      // never fights the positioning.
      const g = document.createElementNS(SVGNS, 'g');
      g.setAttribute('transform', 'translate(' + x + ',' + y + ')');
      g.setAttribute('class', 'allies-particle');
      const p = document.createElementNS(SVGNS, 'path');
      const R = 6 + Math.random() * 6;
      p.setAttribute('d', starPath(R));
      p.setAttribute('fill', color);
      p.setAttribute('stroke', '#231f20');
      p.setAttribute('stroke-width', '0.9');
      g.appendChild(p);
      fxLayer.appendChild(g);
      p.style.transformBox = 'fill-box';
      p.style.transformOrigin = 'center';
      const driftX = (Math.random() - 0.5) * 60;
      const pop = 14 + Math.random() * 16;     // small initial blow-out upward
      const fall = 70 + Math.random() * 80;    // then falls back down the stick
      const spin = (Math.random() - 0.5) * 540;
      const dur = 1500 + Math.random() * 900;
      const a = p.animate([
        { transform: 'translate(0px,0px) scale(0.2) rotate(0deg)', opacity: 0 },
        { transform: 'translate(' + (driftX * 0.4) + 'px,' + (-pop) + 'px) scale(1) rotate(' + (spin * 0.5) + 'deg)', opacity: 1, offset: 0.25 },
        { transform: 'translate(' + driftX + 'px,' + fall + 'px) scale(0.85) rotate(' + spin + 'deg)', opacity: 0 }
      ], { duration: dur, easing: 'cubic-bezier(.25,.55,.45,1)' });
      a.onfinish = function () { g.remove(); };
    }

    /* =====================================================================
       7) TOP MEGAPHONES — star confetti from the yellow horn mouths beside
       the clocks (.allies-megaphone-mouth-left / -right), not laptop or side mics.
       ===================================================================== */
    function animateSpeakers() {
      const mouths = [
        { el: svg.querySelector('.allies-megaphone-mouth-left'), dxMin: 30, dxMax: 110, popMin: 18, popMax: 42 },
        { el: svg.querySelector('.allies-megaphone-mouth-right'), dxMin: -110, dxMax: -30, popMin: 18, popMax: 42 }
      ];
      const emitters = mouths.filter(function (m) { return m.el; }).map(function (m) {
        const b = m.el.getBBox();
        return {
          x: b.x + b.width / 2,
          y: b.y + b.height / 2,
          dxMin: m.dxMin,
          dxMax: m.dxMax,
          popMin: m.popMin,
          popMax: m.popMax
        };
      });
      if (!emitters.length) return;

      const palette = ['#15a091', '#ffd100', '#ec1e24', '#00a551', '#11a0cc', '#f6921e', '#672e90', '#ffffff'];
      emitters.forEach(function (emitter, i) {
        function sprinkle() {
          const n = 2 + Math.floor(Math.random() * 3);
          for (let k = 0; k < n; k++) {
            emitSprinkle(emitter, palette[Math.floor(Math.random() * palette.length)]);
          }
          setTimeout(sprinkle, 320 + Math.random() * 620);
        }
        setTimeout(sprinkle, i * 180 + Math.random() * 420);
      });
    }

    function emitSprinkle(emitter, color) {
      const g = document.createElementNS(SVGNS, 'g');
      g.setAttribute('transform', 'translate(' + emitter.x + ',' + emitter.y + ')');
      g.setAttribute('class', 'allies-particle');
      const p = document.createElementNS(SVGNS, 'path');
      const R = 11 + Math.random() * 9;
      p.setAttribute('d', starPath(R));
      p.setAttribute('fill', color);
      p.setAttribute('stroke', '#231f20');
      p.setAttribute('stroke-width', '1.1');
      g.appendChild(p);
      fxLayer.appendChild(g);
      p.style.transformBox = 'fill-box';
      p.style.transformOrigin = 'center';
      const driftX = emitter.dxMin + Math.random() * (emitter.dxMax - emitter.dxMin);
      const pop = emitter.popMin + Math.random() * (emitter.popMax - emitter.popMin);
      const fall = 55 + Math.random() * 75;
      const spin = (Math.random() - 0.5) * 720;
      const dur = 1500 + Math.random() * 900;
      const a = p.animate([
        { transform: 'translate(0px,0px) scale(0.35) rotate(0deg)', opacity: 0 },
        { transform: 'translate(' + (driftX * 0.45) + 'px,' + (-pop) + 'px) scale(1) rotate(' + (spin * 0.35) + 'deg)', opacity: 1, offset: 0.22 },
        { transform: 'translate(' + driftX + 'px,' + fall + 'px) scale(0.9) rotate(' + spin + 'deg)', opacity: 0 }
      ], { duration: dur, easing: 'cubic-bezier(.3,.5,.5,1)' });
      a.onfinish = function () { g.remove(); };
    }

    /* =====================================================================
       8) SIDE DESIGNS — right 1→2→3 and left 1→2→3 in parallel; each pair
       expands from the center outward (not from the screen edges).
       ===================================================================== */
    function animateSideDesigns() {
      const duration = 420;
      const pause = 200;
      const inward = 140;

      function wait(ms) {
        return new Promise(function (resolve) { setTimeout(resolve, ms); });
      }

      function revealItem(el, fromX) {
        return new Promise(function (resolve) {
          if (!el) { resolve(); return; }
          let b;
          try { b = el.getBBox(); } catch (e) { resolve(); return; }
          const ox = b.x + b.width / 2;
          const oy = b.y + b.height / 2;
          el.style.transformBox = 'view-box';
          el.style.transformOrigin = ox + 'px ' + oy + 'px';
          tag(el, 'side-design');
          const anim = el.animate([
            {
              opacity: 0,
              visibility: 'hidden',
              transform: 'translate(' + fromX + 'px, 0px) scale(0.35)'
            },
            {
              opacity: 1,
              visibility: 'visible',
              transform: 'translate(0px, 0px) scale(1)',
              offset: 0.72
            },
            {
              opacity: 1,
              visibility: 'visible',
              transform: 'translate(0px, 0px) scale(1)'
            }
          ], {
            duration: duration,
            easing: 'cubic-bezier(.22,1,.36,1)',
            fill: 'forwards'
          });
          anim.onfinish = resolve;
        });
      }

      const rightItems = [
        '.allies-right-design-item-1',
        '.allies-right-design-item-2',
        '.allies-right-design-item-3'
      ];
      const leftItems = [
        '.allies-left-design-item-1',
        '.allies-left-design-item-2',
        '.allies-left-design-item-3'
      ];

      (async function run() {
        for (let i = 0; i < 3; i++) {
          await Promise.all([
            revealItem(svg.querySelector(rightItems[i]), -inward),
            revealItem(svg.querySelector(leftItems[i]), inward)
          ]);
          if (i < 2) await wait(pause);
        }
      })();
    }

    /* =====================================================================
       8b) FLOWERS — yellow + blue per step slide out together (parallel).
       Whole flower group moves from centre outward in one smooth motion.
       ===================================================================== */
    function animateFlowers() {
      const duration = 760;
      const groupGap = 220;
      const centerX = 834;
      const centerY = 1267;
      const slide = 0.52;

      function wait(ms) {
        return new Promise(function (resolve) { setTimeout(resolve, ms); });
      }

      function revealFlower(el) {
        if (!el) return;
        let b;
        try { b = el.getBBox(); } catch (e) { return; }
        const ox = b.x + b.width / 2;
        const oy = b.y + b.height / 2;
        const fromX = (centerX - ox) * slide;
        const fromY = (centerY - oy) * slide;
        el.style.transformBox = 'view-box';
        el.style.transformOrigin = ox + 'px ' + oy + 'px';
        tag(el, 'flower');
        el.animate([
          {
            opacity: 0,
            visibility: 'hidden',
            transform: 'translate(' + fromX + 'px,' + fromY + 'px) scale(0.9)'
          },
          {
            opacity: 1,
            visibility: 'visible',
            transform: 'translate(' + (fromX * 0.05) + 'px,' + (fromY * 0.05) + 'px) scale(1.02)',
            offset: 0.8
          },
          {
            opacity: 1,
            visibility: 'visible',
            transform: 'translate(0px,0px) scale(1)'
          }
        ], {
          duration: duration,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'forwards'
        });
      }

      const groups = [
        ['.allies-l-yellow-flower-1', '.allies-r-yellow-flower-1', '.allies-l-blue-flower-1', '.allies-r-blue-flower-1'],
        ['.allies-l-yellow-flower-2', '.allies-r-yellow-flower-2', '.allies-l-blue-flower-2', '.allies-r-blue-flower-2'],
        ['.allies-l-yellow-flower-3', '.allies-r-yellow-flower-3', '.allies-l-blue-flower-3', '.allies-r-blue-flower-3']
      ];

      (async function run() {
        for (let i = 0; i < groups.length; i++) {
          groups[i].forEach(function (sel) {
            revealFlower(svg.querySelector(sel));
          });
          if (i < groups.length - 1) await wait(groupGap);
        }
      })();
    }

    /* =====================================================================
       8c) LIVE IST CLOCKS — custom pointed SVG hands (like banner script),
       smooth requestAnimationFrame updates with fractional seconds.
       ===================================================================== */
    function animateIndianClocks() {
      const CLOCKS = [
        { name: 'flower-left', cx: 586.56, cy: 1198.69, r: 50.42 },
        { name: 'flower-right', cx: 1081.31, cy: 1198.25, r: 50.42 },
        { name: 'center', cx: 834.72, cy: 45.4, r: 43.56, hourColor: '#f6921e', minuteColor: '#231f20' },
        { name: 'right-upper', cx: 938.02, cy: 75.14, r: 43.56 },
        { name: 'right-lower', cx: 1043.11, cy: 106.01, r: 43.56 },
        { name: 'left-upper', cx: 731.17, cy: 74.67, r: 43.56 },
        { name: 'left-lower', cx: 626.09, cy: 105.55, r: 43.56 }
      ];

      svg.querySelectorAll('.allies-clock-hand').forEach(function (legacy) {
        legacy.style.opacity = '0';
        legacy.style.visibility = 'hidden';
        legacy.style.pointerEvents = 'none';
      });

      function roundCoord(value) {
        return Math.round(value * 100) / 100;
      }

      function pointedHandPath(center, length, width) {
        const cx = center.x;
        const cy = center.y;
        const tipY = cy - length;
        const headLength = Math.min(length * 0.32, Math.max(width * 1.4, 1.55));
        const neckY = tipY + headLength;
        const shaftHalf = Math.max(width * 0.2, 0.28);
        const headHalf = Math.max(width * 0.62, 0.72);
        const tailY = cy + Math.max(width * 0.28, 0.36);
        return [
          'M', roundCoord(cx), roundCoord(tipY),
          'C', roundCoord(cx + headHalf * 0.9), roundCoord(tipY + headLength * 0.22),
          roundCoord(cx + headHalf), roundCoord(neckY - headLength * 0.18),
          roundCoord(cx + shaftHalf), roundCoord(neckY),
          'L', roundCoord(cx + shaftHalf), roundCoord(tailY),
          'L', roundCoord(cx - shaftHalf), roundCoord(tailY),
          'L', roundCoord(cx - shaftHalf), roundCoord(neckY),
          'C', roundCoord(cx - headHalf), roundCoord(neckY - headLength * 0.18),
          roundCoord(cx - headHalf * 0.9), roundCoord(tipY + headLength * 0.22),
          roundCoord(cx), roundCoord(tipY),
          'Z'
        ].join(' ');
      }

      function lineHand(center, length, width, color) {
        const line = document.createElementNS(SVGNS, 'line');
        line.setAttribute('x1', String(center.x));
        line.setAttribute('y1', String(center.y));
        line.setAttribute('x2', String(center.x));
        line.setAttribute('y2', String(center.y - length));
        line.setAttribute('stroke', color || '#231f20');
        line.setAttribute('stroke-width', String(width));
        line.setAttribute('stroke-linecap', 'round');
        return line;
      }

      function setHandRotation(el, angle, cx, cy) {
        el.setAttribute('transform', 'rotate(' + angle + ' ' + cx + ' ' + cy + ')');
      }

      function buildClock(cfg) {
        const center = { x: cfg.cx, y: cfg.cy };
        const r = cfg.r;
        const group = document.createElementNS(SVGNS, 'g');
        group.setAttribute('class', 'allies-clock-hands allies-clock-' + cfg.name);
        group.style.pointerEvents = 'none';

        const hourColor = cfg.hourColor || '#231f20';
        const minuteColor = cfg.minuteColor || '#231f20';

        const hour = document.createElementNS(SVGNS, 'path');
        hour.setAttribute('class', 'allies-clock-hour');
        hour.setAttribute('d', pointedHandPath(center, r * 0.43, Math.max(r * 0.085, 1.1)));
        hour.setAttribute('fill', hourColor);
        hour.setAttribute('stroke', hourColor);
        hour.setAttribute('stroke-width', String(Math.max(r * 0.014, 0.18)));
        hour.setAttribute('stroke-linejoin', 'round');
        tag(hour, 'clock');

        const minute = document.createElementNS(SVGNS, 'path');
        minute.setAttribute('class', 'allies-clock-minute');
        minute.setAttribute('d', pointedHandPath(center, r * 0.61, Math.max(r * 0.065, 0.85)));
        minute.setAttribute('fill', minuteColor);
        minute.setAttribute('stroke', minuteColor);
        minute.setAttribute('stroke-width', String(Math.max(r * 0.011, 0.16)));
        minute.setAttribute('stroke-linejoin', 'round');
        tag(minute, 'clock');

        group.appendChild(hour);
        group.appendChild(minute);

        let second = null;
        if (!cfg.staticTime) {
          second = lineHand(center, r * 0.74, 0.55, '#e6174f');
          second.setAttribute('class', 'allies-clock-second');
          tag(second, 'clock');
          group.appendChild(second);
        }

        const pin = document.createElementNS(SVGNS, 'circle');
        pin.setAttribute('class', 'allies-clock-pin');
        pin.setAttribute('cx', String(cfg.cx));
        pin.setAttribute('cy', String(cfg.cy));
        pin.setAttribute('r', String(Math.max(0.8, r * 0.09)));
        pin.setAttribute('fill', '#231f20');

        group.appendChild(pin);
        fxLayer.appendChild(group);

        return { hour: hour, minute: minute, second: second, cx: cfg.cx, cy: cfg.cy, cfg: cfg };
      }

      const allHands = CLOCKS.map(buildClock);
      const liveHands = [];
      const spinHands = [];
      CLOCKS.forEach(function (cfg, i) {
        if (cfg.staticTime) return;
        if (cfg.spin) spinHands.push(allHands[i]);
        else liveHands.push(allHands[i]);
      });

      function syncStaticClocks() {
        allHands.forEach(function (hands) {
          const t = hands.cfg.staticTime;
          if (!t) return;
          const hourAngle = (t.hour % 12) * 30 + t.minute * 0.5;
          const minuteAngle = t.minute * 6;
          setHandRotation(hands.minute, minuteAngle, hands.cx, hands.cy);
          setHandRotation(hands.hour, hourAngle, hands.cx, hands.cy);
        });
      }

      const spinStart = performance.now();

      function getISTInstant() {
        const now = new Date();
        const parts = { hour: 0, minute: 0, second: 0 };
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).formatToParts(now).forEach(function (p) {
          if (p.type in parts) parts[p.type] = Number(p.value);
        });
        const hour24 = parts.hour === 24 ? 0 : parts.hour;
        const ms = now.getMilliseconds();
        const seconds = parts.second + ms / 1000;
        const minutes = parts.minute + seconds / 60;
        const hours = (hour24 % 12) + minutes / 60;
        return { hours: hours, minutes: minutes, seconds: seconds };
      }

      function syncClocks() {
        const t = getISTInstant();
        liveHands.forEach(function (hands) {
          setHandRotation(hands.second, t.seconds * 6, hands.cx, hands.cy);
          setHandRotation(hands.minute, t.minutes * 6, hands.cx, hands.cy);
          setHandRotation(hands.hour, t.hours * 30, hands.cx, hands.cy);
        });
      }

      function syncSpinClocks() {
        const elapsed = (performance.now() - spinStart) / 1000;
        spinHands.forEach(function (hands) {
          setHandRotation(hands.second, (elapsed * 96) % 360, hands.cx, hands.cy);
          setHandRotation(hands.minute, (elapsed * 36) % 360, hands.cx, hands.cy);
          setHandRotation(hands.hour, (elapsed * 12) % 360, hands.cx, hands.cy);
        });
      }

      function tick() {
        syncClocks();
        syncSpinClocks();
        window.__alliesClockFrame = requestAnimationFrame(tick);
      }

      if (window.__alliesClockFrame) {
        cancelAnimationFrame(window.__alliesClockFrame);
      }
      syncStaticClocks();
      syncClocks();
      syncSpinClocks();
      tick();
    }

    /* =====================================================================
       8d) HEADER CHAKRA STICKS — rotate inner star + hand paths only;
       the outer chakra ring (cls-31) and tick marks stay fixed.
       ===================================================================== */
    function animateHeaderChakraSticks() {
      const CHAKRAS = [
        { name: 'header-left', cx: 227.68, cy: 281.9, r: 52 },
        { name: 'header-right', cx: 1439.77, cy: 280.91, r: 52 }
      ];

      function isChakraStick(it, cfg) {
        if (!inBox(it, cfg.cx - cfg.r, cfg.cy - cfg.r, cfg.cx + cfg.r, cfg.cy + cfg.r)) {
          return false;
        }
        if (it.cls === 'cls-88' || it.cls === 'cls-3') return true;
        if (it.el.tagName === 'path' && !it.cls) {
          const d = it.el.getAttribute('d') || '';
          if (d.indexOf('M1454.03') === 0 || d.indexOf('M213.83') === 0) return true;
        }
        return false;
      }

      function setStickRotation(el, angle, cx, cy, baseTransform) {
        const rot = 'rotate(' + angle + ' ' + cx + ' ' + cy + ')';
        el.setAttribute('transform', baseTransform ? baseTransform + ' ' + rot : rot);
      }

      const groups = CHAKRAS.map(function (cfg) {
        const sticks = pick(function (it) { return isChakraStick(it, cfg); });
        sticks.forEach(function (it) { tag(it.el, 'chakra-stick'); });
        return {
          cx: cfg.cx,
          cy: cfg.cy,
          sticks: sticks,
          baseTransforms: sticks.map(function (it) {
            return it.el.getAttribute('transform') || '';
          })
        };
      });

      const spinStart = performance.now();

      function tick() {
        const elapsed = (performance.now() - spinStart) / 1000;
        const angle = (elapsed * 18) % 360;
        groups.forEach(function (grp) {
          grp.sticks.forEach(function (it, i) {
            setStickRotation(it.el, angle, grp.cx, grp.cy, grp.baseTransforms[i]);
          });
        });
        window.__alliesHeaderChakraFrame = requestAnimationFrame(tick);
      }

      if (window.__alliesHeaderChakraFrame) {
        cancelAnimationFrame(window.__alliesHeaderChakraFrame);
      }
      tick();
    }

    /* =====================================================================
       8e) CENTER MANDALA BLADE — yellow star at ~844,1630; two paths only.
       ===================================================================== */
    function animateCenterBlade() {
      const BLADE_D = ['M844.89,1630.16', 'M843.27,1631.11'];
      const pivotX = 844.08;
      const pivotY = 1630.63;

      const blades = pick(function (it) {
        if (it.el.tagName !== 'path') return false;
        const d = it.el.getAttribute('d') || '';
        return BLADE_D.some(function (sig) { return d.indexOf(sig) === 0; });
      });

      if (!blades.length) return;

      const baseTransforms = blades.map(function (it) {
        tag(it.el, 'center-blade');
        return it.el.getAttribute('transform') || '';
      });

      const spinStart = performance.now();

      function tick() {
        const angle = ((performance.now() - spinStart) / 1000) * 8 % 360;
        blades.forEach(function (it, i) {
          const rot = 'rotate(' + angle + ' ' + pivotX + ' ' + pivotY + ')';
          const base = baseTransforms[i];
          it.el.setAttribute('transform', base ? base + ' ' + rot : rot);
        });
        window.__alliesBladeFrame = requestAnimationFrame(tick);
      }

      if (window.__alliesBladeFrame) {
        cancelAnimationFrame(window.__alliesBladeFrame);
      }
      tick();
    }

    /* =====================================================================
       9) CHARACTER CLOUDS — scale outward, then a fixed set of rain strokes.
       ===================================================================== */
    function animateCharacterClouds(delay) {
      const rainLayer = document.createElementNS(SVGNS, 'g');
      rainLayer.setAttribute('id', 'allies-rain-layer');
      rainLayer.setAttribute('class', 'allies-rain');
      fxLayer.appendChild(rainLayer);

      const RAIN_DROPS = 6;
      const cloudConfigs = [];

      [
        { sel: '.allies-right-cloud' },
        { sel: '.allies-left-cloud' }
      ].forEach(function (cfg, index) {
        const cloud = svg.querySelector(cfg.sel);
        if (!cloud) return;

        let b;
        try { b = cloud.getBBox(); } catch (e) { return; }
        const pivotX = b.x + b.width / 2;
        const pivotY = b.y + b.height / 2;
        cloud.style.transformBox = 'view-box';
        cloud.style.transformOrigin = pivotX + 'px ' + pivotY + 'px';
        tag(cloud, 'cloud');

        const cloudDelay = delay + index * 100;
        cloud.animate([
          { transform: 'scale(1)' },
          { transform: 'scale(1.04)', offset: 0.5 },
          { transform: 'scale(1)' }
        ], {
          duration: 3200,
          delay: cloudDelay,
          iterations: Infinity,
          easing: 'ease-in-out'
        });

        cloudConfigs.push({
          pivotX: pivotX,
          spread: Math.max(28, b.width * 0.24),
          baseY: b.y + b.height * 0.95,
          rainStart: cloudDelay + 320
        });
      });

      if (!cloudConfigs.length) return;

      setTimeout(function () {
        for (let i = 0; i < RAIN_DROPS; i++) {
          const cfg = cloudConfigs[i % cloudConfigs.length];
          const slot = Math.floor(i / cloudConfigs.length);
          const x = cfg.pivotX + (Math.random() - 0.5) * cfg.spread * 1.4;
          const y = cfg.baseY + slot * 5 + Math.random() * 4;
          emitRainDrop(rainLayer, x, y, 50 + Math.random() * 45);
        }
      }, Math.max.apply(null, cloudConfigs.map(function (c) { return c.rainStart; })));
    }

    function emitRainDrop(layer, x, y, fallDistance) {
      const len = 12 + Math.random() * 12;
      const drift = (Math.random() - 0.5) * 10;
      const opacity = 0.55 + Math.random() * 0.35;
      const line = document.createElementNS(SVGNS, 'line');
      line.setAttribute('x1', String(x));
      line.setAttribute('y1', String(y));
      line.setAttribute('x2', String(x + drift * 0.2));
      line.setAttribute('y2', String(y + len));
      line.setAttribute('stroke', '#2b7fd4');
      line.setAttribute('stroke-width', '3.2');
      line.setAttribute('stroke-linecap', 'round');
      line.setAttribute('opacity', String(opacity));
      layer.appendChild(line);

      const fall = fallDistance || (60 + Math.random() * 40);
      const dur = 520 + Math.random() * 360;
      const x1 = x;
      let y1 = y;
      const x2 = x + drift;
      const y2 = y + fall;
      let start = 0;

      function frame(ts) {
        if (!start) start = ts;
        const t = Math.min(1, (ts - start) / dur);
        const ease = 1 - Math.pow(1 - t, 2);
        const cy = y1 + (y2 - y1) * ease;
        const cx = x1 + (x2 - x1) * ease;
        line.setAttribute('x1', String(cx));
        line.setAttribute('y1', String(cy - len));
        line.setAttribute('x2', String(cx + drift * 0.15));
        line.setAttribute('y2', String(cy));
        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          start = 0;
          y1 = y - Math.random() * 8;
          requestAnimationFrame(frame);
        }
      }
      requestAnimationFrame(frame);
    }

    /* =====================================================================
       10) DIAMOND LEDs — left & right columns blink together in sync.
       ===================================================================== */
    function animateDiamondBlink(delay) {
      const RED = '#ec1e24';
      const DIM = 0.38;
      const BRIGHT = 1;
      const BLINK_MS = 720;

      const leftLeds = svg.querySelectorAll('.allies-led-column-left .allies-led');
      const rightLeds = svg.querySelectorAll('.allies-led-column-right .allies-led');
      const all = [];
      leftLeds.forEach(function (el) { all.push(el); });
      rightLeds.forEach(function (el) { all.push(el); });
      if (!all.length) return;

      all.forEach(function (el) {
        el.setAttribute('fill', RED);
        el.style.opacity = String(DIM);
        tag(el, 'diamond-led');
      });

      function blink() {
        all.forEach(function (el) {
          el.animate([
            { opacity: DIM },
            { opacity: BRIGHT, offset: 0.42 },
            { opacity: DIM }
          ], { duration: BLINK_MS, easing: 'ease-in-out' });
        });
      }

      setTimeout(function () {
        blink();
        if (window.__alliesDiamondTimer) clearInterval(window.__alliesDiamondTimer);
        window.__alliesDiamondTimer = setInterval(blink, BLINK_MS);
      }, delay);
    }

    /* =====================================================================
       11) BANNER STICKS — purple / yellow / teal fan bars left & right of
           the ALLIES MATTER strip (cls-88, cls-38, cls-90 rects ~y 1774).
       ===================================================================== */
    function animateBannerSticks() {
      const sticks = pick(function (it) {
        return it.el.tagName === 'rect' &&
          it.y >= 1770 && it.y <= 1795 &&
          it.h >= 105 && it.h <= 115 &&
          (it.cls === 'cls-88' || it.cls === 'cls-38' || it.cls === 'cls-90');
      });

      if (!sticks.length) return;

      sticks.sort(function (a, b) { return a.cx - b.cx; });

      sticks.forEach(function (it, i) {
        const pivotY = it.y + it.h;
        origin(it.el, it.cx, pivotY);
        tag(it.el, 'banner-stick');

        const side = it.cx < 834 ? -1 : 1;
        const group = sticks.filter(function (s) {
          return side < 0 ? s.cx < 834 : s.cx > 834;
        });
        const order = group.indexOf(it);
        const baseAngle = side * (order - 1) * 6;

        function sway() {
          it.el.animate([
            { transform: 'rotate(' + baseAngle + 'deg) scale(1)' },
            {
              transform: 'rotate(' + (baseAngle + side * 14) + 'deg) scale(1.04)',
              offset: 0.5
            },
            { transform: 'rotate(' + baseAngle + 'deg) scale(1)' }
          ], { duration: 700 + order * 130, easing: 'ease-in-out' });
        }

        setTimeout(function () {
          sway();
          setInterval(sway, 1100 + order * 180);
        }, 500 + i * 90);
      });
    }

    /* =====================================================================
       12) KEYBOARDS — laptop keys + side piano strips type with key presses.
       ===================================================================== */
    function animateKeyboardTyping() {
      const keys = [];
      const KEY_COLORS = [
        '#ffd100', '#ec1e24', '#672e90', '#11a0cc',
        '#00a551', '#f6921e', '#ef8200', '#be4c97'
      ];

      pick(function (it) {
        return it.el.tagName === 'rect' && it.cls === 'cls-28' &&
          it.cy >= 1775 && it.cy <= 1885 && (it.cx < 520 || it.cx > 1140);
      }).forEach(function (it) { keys.push(it); });

      pick(function (it) {
        return inBox(it, 688, 1343, 972, 1398) &&
          it.h <= 14 && it.w >= 12 && it.w <= 180 &&
          it.cls !== 'cls-35' && it.cls !== 'cls-21' && it.cls !== 'cls-3' &&
          it.cls !== 'cls-97';
      }).forEach(function (it) { keys.push(it); });

      if (!keys.length) return;

      keys.sort(function (a, b) {
        return a.cy - b.cy || a.cx - b.cx;
      });

      let index = 0;
      let colorIndex = 0;

      function pressKey() {
        const it = keys[index % keys.length];
        index += 1;
        const el = it.el;
        const origFill = el.getAttribute('fill');
        const color = KEY_COLORS[colorIndex % KEY_COLORS.length];
        colorIndex += 1;

        origin(el, it.cx, it.cy);
        tag(el, 'keyboard-key');
        el.setAttribute('fill', color);

        const anim = el.animate([
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 1, transform: 'scale(0.88)', offset: 0.35 },
          { opacity: 1, transform: 'scale(1)' }
        ], { duration: 110, easing: 'ease-out' });

        anim.onfinish = function () {
          if (origFill) el.setAttribute('fill', origFill);
          else el.removeAttribute('fill');
        };
      }

      setInterval(pressKey, 95);
    }
  }
	function boot() {
		requestAnimationFrame(function () {
			requestAnimationFrame(start);
		});
	}

	boot();
}